import type { BudgetPayrollProjectionCode } from '@alinea/types';

export const budgetPtBr = {
  title: 'Orçamento',
  list: {
    title: 'Orçamentos',
    newBudget: 'Novo orçamento',
    empty: 'Nenhum orçamento cadastrado',
    count: '{{count}} orçamentos',
    statusFilter: 'Status',
    createTitle: 'Novo orçamento',
    createDescription: 'Crie um plano de orçamento em rascunho para a entidade selecionada.',
    duplicateTitle: 'Duplicar orçamento',
    duplicateDescription: 'Uma cópia em rascunho será criada com as mesmas linhas.',
    columns: {
      name: 'Nome',
      status: 'Status',
      effective: 'Vigência',
      totalLimit: 'Limite total',
      lineCount: 'Linhas',
      active: 'Ativo',
      actions: 'Ações',
    },
    actions: {
      open: 'Abrir',
      activate: 'Ativar',
      promoteReforecast: 'Aprovar reforecast',
      createReforecast: 'Criar reforecast',
      duplicate: 'Duplicar',
      archive: 'Arquivar',
    },
    fields: {
      name: 'Nome',
      description: 'Descrição',
      startPeriod: 'Período inicial',
      endPeriod: 'Período final',
      openEndedEnd: 'Sem período final (vigência aberta)',
      effectiveFrom: 'Vigência inicial',
      effectiveUntil: 'Vigência final',
    },
    emptyState: {
      title: 'Comece a controlar seus gastos',
      description:
        'Crie um orçamento para definir limites de gasto por centro de custo, departamento ou categoria. O Alchemy acompanha o realizado em tempo real e alerta sua equipe antes de qualquer estouro.',
      createFirst: 'Criar primeiro orçamento',
      howItWorks: 'Como funciona',
      howItWorksTitle: 'Como funciona o controle de orçamento',
      howItWorksDescription:
        'Orçamentos ajudam o financeiro a definir limites e acompanhar gastos antes que eles estourem.',
      howItWorksSteps: {
        create: 'Crie um plano de orçamento por entidade e livro contábil.',
        lines: 'Adicione linhas por centro de custo, departamento ou conta gerencial.',
        track: 'O Alchemy compara realizado e comprometido com o limite configurado.',
        enforce: 'Alertas e bloqueios podem ser configurados por linha ou globalmente.',
      },
      templatesHeading: 'Ou comece a partir de um modelo',
      templates: {
        cost_center: {
          title: 'Por centro de custo',
          description: 'Um limite anual para cada centro de custo da empresa.',
        },
        department: {
          title: 'Por departamento',
          description: 'Distribua o orçamento entre as áreas da organização.',
        },
        managerial_account: {
          title: 'Por plano de contas gerencial',
          description:
            'Uma linha para cada conta gerencial de despesa, seguindo a hierarquia do plano.',
        },
      },
      guidePrefix: 'Novo no controle de orçamentos?',
      guideLink: 'Veja o guia rápido',
    },
    createTemplate: {
      heading: 'Modelo de linhas',
      description: 'Opcional. Gere linhas automaticamente a partir da estrutura da entidade.',
      options: {
        none: {
          title: 'Em branco',
          description: 'Crie o plano sem linhas e adicione-as manualmente depois.',
        },
        cost_center: {
          title: 'Por centro de custo',
          description: 'Uma linha para cada centro de custo ativo da entidade.',
        },
        department: {
          title: 'Por departamento',
          description: 'Uma linha para cada departamento ativo da entidade.',
        },
        managerial_account: {
          title: 'Por plano de contas gerencial',
          description:
            'Uma linha para cada conta gerencial de despesa, seguindo a hierarquia do plano.',
        },
      },
    },
    templateCreate: {
      cost_center: {
        title: 'Linhas por centro de custo',
        description:
          'Ao salvar, o orçamento será criado com uma linha para cada centro de custo ativo da entidade. Os limites serão definidos na tela de detalhes.',
      },
      department: {
        title: 'Linhas por departamento',
        description:
          'Ao salvar, o orçamento será criado com uma linha para cada departamento ativo da entidade. Os limites serão definidos na tela de detalhes.',
      },
      managerial_account: {
        title: 'Linhas por plano de contas gerencial',
        description:
          'Ao salvar, o orçamento será criado com uma linha para cada conta gerencial de despesa ativa da entidade, seguindo a hierarquia do plano. Os limites serão definidos na tela de detalhes.',
      },
    },
    validation: {
      required: 'Campo obrigatório',
      completeRequired: 'Preencha todos os campos obrigatórios',
    },
    filteredEmpty: 'Nenhum orçamento encontrado para os filtros selecionados',
  },
  planStatus: {
    DRAFT: 'Rascunho',
    ACTIVE: 'Ativo',
    ARCHIVED: 'Arquivado',
    SUPERSEDED: 'Substituído',
  },
  bulk: {
    selectAll: 'Selecionar todas as linhas visíveis',
    selectLine: 'Selecionar linha de orçamento',
    clearSelection: 'Limpar seleção',
    selected: '{{count}} linhas selecionadas',
    selectedLimit: 'Limite selecionado',
    actions: {
      totalLimit: 'Limite total',
      periodLimit: 'Limite período',
      totalMode: 'Modo total',
      periodMode: 'Modo período',
      menu: 'Ações em lote',
      archive: 'Excluir linhas',
      applying: 'Aplicando...',
      confirm: 'Confirmar alterações',
    },
    fields: {
      operation: 'Operação',
      value: 'Valor',
      emptyClears: 'Em branco remove o limite',
    },
    operation: {
      SET: 'Definir valor exato',
      INCREASE_AMOUNT: 'Aumentar valor',
      DECREASE_AMOUNT: 'Reduzir valor',
      INCREASE_PERCENT: 'Aumentar percentual',
      DECREASE_PERCENT: 'Reduzir percentual',
    },
    dialog: {
      archive: {
        title: 'Excluir linhas selecionadas?',
        description: 'As {{count}} linhas serão arquivadas e deixarão de afetar o orçamento.',
      },
      amount: {
        title: 'Alterar limite total?',
        description: 'Esta alteração será aplicada em {{count}} linhas selecionadas.',
      },
      perPeriodAmount: {
        title: 'Alterar limite por período?',
        description: 'Esta alteração será aplicada em {{count}} linhas selecionadas.',
      },
      enforcementMode: {
        title: 'Alterar controle do limite total?',
        description: 'Esta alteração será aplicada em {{count}} linhas selecionadas.',
      },
      perPeriodEnforcementMode: {
        title: 'Alterar controle por período?',
        description: 'Esta alteração será aplicada em {{count}} linhas selecionadas.',
      },
    },
  },
  history: {
    title: 'Histórico',
    description: 'Veja quem alterou o orçamento e suas linhas.',
    empty: 'Nenhuma alteração registrada ainda.',
    entity: {
      BUDGET: 'Orçamento',
      BUDGET_LINE: 'Linha',
    },
    action: {
      CREATE: 'Criação',
      UPDATE: 'Atualização',
      ARCHIVE: 'Arquivamento',
      ACTIVATE: 'Ativação',
      DUPLICATE: 'Duplicação',
      CREATE_REVISION: 'Criação de reforecast',
      PROMOTE_REVISION: 'Promoção de reforecast',
      BULK_UPDATE: 'Atualização em massa',
      BULK_ARCHIVE: 'Arquivamento em massa',
    },
    field: {
      name: 'Nome',
      description: 'Descrição',
      status: 'Status',
      amount: 'Limite total',
      perPeriodAmount: 'Limite por período',
      enforcementMode: 'Controle do limite total',
      perPeriodEnforcementMode: 'Controle por período',
      glAccountId: 'Conta contábil',
      effectiveFrom: 'Vigência inicial',
      effectiveUntil: 'Vigência final',
      sourceBudgetId: 'Origem',
      updatedByUserId: 'Usuário de alteração',
    },
  },
  subtitle: 'Acompanhe limites, utilização, alertas e exceções por dimensão',
  breadcrumbs: {
    accounting: 'Contabilidade',
    fpa: 'FP&A',
    budget: 'Orçamento',
  },
  spreadsheet: {
    title: 'Planilha de orçamento',
    backToDetails: 'Voltar aos detalhes',
    anyDimension: '— (qualquer) —',
    addRow: 'Adicionar linha',
    addRows: '+{{count}} linhas',
    deleteRow: 'Remover linha',
    fillRight: 'Replicar p/direita',
    undo: 'Desfazer',
    redo: 'Refazer',
    undoHint: 'Desfazer (Ctrl+Z)',
    redoHint: 'Refazer (Ctrl+Shift+Z)',
    clearValues: 'Limpar valores',
    periodTotals: 'Total por período',
    navigationHint: 'navegar ·',
    rowSelectionHint: 'clique no # para selecionar linha ·',
    pasteHint: 'colar do Excel',
    segmentColumnDescription: 'Campo customizado da entidade',
    status: {
      dirty: 'Alterações não salvas',
      saved: 'Tudo salvo',
    },
    toolbar: {
      columns: 'Colunas',
      filters: 'Filtros',
      dimensionColumns: 'Dimensões da linha',
      rowDimensions: 'Dimensões da linha',
      filterDimensions: 'Filtrar dimensões',
      filterPlaceholder: 'Buscar por centro, departamento, conta…',
      moveUp: 'Mover coluna para cima',
      moveDown: 'Mover coluna para baixo',
      columnHint: 'Mostre, oculte e reordene as colunas de dimensão.',
      statusSelector: 'Status ({{count}})',
    },
    alerts: {
      toolbarButton: 'Alertas & solicitações ({{count}})',
      panelTitle: 'Alertas e solicitações',
      panelDescription: 'Exceções pendentes e linhas em alerta nesta planilha.',
      inlineRequestedAt: 'Exceção solicitada em {{date}}',
      requestedBy: 'Solicitado por {{name}}',
      amountRequested: 'Excesso: {{amount}}',
      sourceBill: 'Fatura',
      sourcePurchaseRequest: 'Solicitação de compra',
      sourcePurchaseOrder: 'Pedido de compra',
      sourceRecurringBill: 'Conta recorrente',
    },
    tags: {
      gl: 'Razão',
      mng: 'Gerencial',
      segment: 'Campo',
    },
    columns: {
      rowTotal: 'Total linha',
      enforcement: 'Enforcement',
      totalBudget: 'Total período',
      totalBudgetHint: 'real + compr.',
      available: 'Saldo',
      availableHint: 'livre',
    },
    dimensionDescriptions: {
      costCenter: 'Marketing, TI, Operações…',
      department: 'Hierarquia organizacional',
      managerialAccount: 'Categoria gerencial',
      glAccount: 'Plano de contas (razão)',
    },
    kpis: {
      totalBudgeted: 'Total orçado',
      lineCount: '{{count}} linhas',
      filledLines: 'Linhas preenchidas',
      filledLinesHint: 'com ao menos um valor',
      largestLine: 'Maior linha',
      averagePerPeriod: 'Média / período',
      periodCount: 'em {{count}} períodos',
    },
    howItWorks: {
      title: 'Como funciona',
      description:
        'Cada linha é a interseção das dimensões preenchidas — deixe uma dimensão vazia para não restringir por ela. Linhas mais específicas aninham-se sob linhas-pai mais amplas: o pai define o teto e as filhas alocam dentro dele. Use o agrupamento hierárquico para ver Σ filhas vs teto e sincronizar quando necessário.',
    },
    footer: {
      summary: '{{count}} linhas · Total orçado {{total}}',
    },
    actions: {
      saveDraft: 'Salvar rascunho',
      activateBudget: 'Ativar orçamento',
      promoteReforecast: 'Aprovar reforecast',
      createReforecast: 'Criar reforecast',
      promotingReforecast: 'Aprovando reforecast…',
      inactivateBudget: 'Inativar orçamento',
      saving: 'Salvando…',
      activating: 'Ativando…',
      inactivatingBudget: 'Inativando…',
      resync: 'Resincronizar',
      resyncing: 'Resincronizando…',
      openSpreadsheet: 'Modo planilha',
      lineOwners: 'Donos de linhas de orçamento',
      fullscreen: 'Tela inteira',
      exitFullscreen: 'Sair da tela inteira',
      alertConfig: 'Config. Alertas',
      editValues: 'Editar valores',
      discardChanges: 'Descartar alterações',
    },
    resync: {
      title: 'Resincronizar compromissos?',
      description:
        'O processamento será executado em segundo plano. Os compromissos de pedidos e contas recorrentes serão reconstruídos, contas realizadas serão liquidadas e duplicidades obsoletas serão canceladas antes de recalcular os totais. Os limites do orçamento não serão alterados.',
      success:
        'Resincronização iniciada. Os números serão atualizados automaticamente quando o processamento terminar.',
    },
    lineOwners: {
      title: 'Donos de linhas de orçamento',
      description:
        'Defina responsáveis por linha para receber alertas de orçamento. O dono do orçamento pode autorizar alterações de limite.',
      budgetOwner: 'Dono do orçamento',
      owner: 'Responsável',
      lineLimit: 'Limite da linha: {{amount}}',
      canModifyLimits: 'Pode modificar limites',
      maxLimit: 'Limite máximo permitido',
      actions: {
        save: 'Salvar',
        saving: 'Salvando…',
        cancel: 'Cancelar',
      },
      messages: {
        saved: 'Responsáveis atualizados',
        saveFailed: 'Não foi possível salvar os responsáveis',
      },
      grantPermission: {
        title: 'Conceder permissão ao usuário?',
        description:
          'O usuário selecionado não possui permissão para gerenciar linhas de orçamento. Deseja atribuir o papel "Responsável por linha de orçamento"?',
        confirm: 'Conceder permissão e salvar',
      },
    },
    display: {
      metricLabel: 'Ver',
      metric: {
        REAL_VS_BUDGET: 'Consumido / Orçado',
        REALIZED: 'Realizado',
        AVAILABLE: 'Saldo',
        UTILIZATION: '% consumo',
        BUDGET: 'Orçado',
      },
      densityLabel: 'Densidade',
      density: {
        COMPACT: 'Compacta',
        STANDARD: 'Padrão',
        COMFORTABLE: 'Ampla',
      },
      unitLabel: 'Unidade',
      unit: {
        FULL: 'F',
        HUNDREDS: 'Centenas',
        THOUSANDS: 'K',
        MILLIONS: 'M',
      },
      unitOption: {
        FULL: 'Valor cheio',
        THOUSANDS: 'Milhares (K)',
        MILLIONS: 'Milhões (M)',
      },
      periodsLabel: 'Períodos',
      periods: {
        all: 'Todos os períodos',
        fromNow: 'Deste mês em diante',
      },
      minTotalPlaceholder: 'Total ≥',
      minTotalClear: 'Limpar filtro de total',
      minTotalApply: 'Aplicar',
      minTotalFilterTooltip: 'Filtrar por total da linha',
      showBars: 'Barras',
      hideZeroRows: 'Ocultar linhas zeradas',
      clearFilters: 'Limpar filtros',
    },
    cellEditor: {
      applyLabel: 'Aplicar',
      applyCell: 'Célula',
      applyRow: 'Linha',
      hint: 'Enter aplica e desce · Tab aplica e avança · Esc cancela',
    },
    messages: {
      saved: 'Orçamento salvo',
      cleared: 'Valores limpos',
      filledRight: 'Valor replicado para a direita',
      filledRow: 'Valor aplicado aos períodos visíveis da linha',
      selectAmountCell: 'Selecione uma célula de valor',
      emptyCell: 'Célula vazia',
      noPeriods:
        'Não há períodos contábeis abertos dentro da vigência deste orçamento para esta entidade/livro. Crie ou abra os períodos antes de editar valores.',
      readOnlyPermission:
        'Você pode visualizar a planilha, mas não tem permissão para editar linhas de orçamento.',
      unsavedModeSwitch: 'Descartar alterações não salvas?',
      unsavedModeSwitchDescription:
        'As alterações locais serão perdidas e a planilha voltará aos últimos valores salvos.',
    },
    mode: {
      view: 'Visualizar',
      edit: 'Editar',
    },
    amountInputPlaceholder: '0,00 · 100k · 1M',
    globalAlerts: {
      title: 'Alertas do rascunho',
      description:
        'Percentuais aplicados às linhas salvas nesta planilha enquanto você edita o orçamento.',
    },
    fillOffer: {
      title: 'Replicar valor?',
      description: 'Replicar {{amount}} para os meses seguintes desta linha?',
      confirm: 'Replicar p/direita',
      dismiss: 'Agora não',
    },
    validation: {
      dimensionRequired: 'Toda linha com valor precisa de ao menos uma dimensão',
      duplicateRows: 'Existem linhas com as mesmas dimensões. Ajuste ou remova a duplicata.',
      hierarchyConflict:
        'A soma das linhas filhas ({{childrenSum}}) excede o limite da linha-pai "{{parent}}" ({{parentAmount}}).',
    },
    hierarchyError: {
      summary:
        'A linha-pai "{{parent}}" ({{parentAmount}}) não cobre a soma das linhas filhas: {{children}} ({{childrenSum}}).',
      generic:
        'A soma das linhas filhas excede o limite da linha-pai. Ajuste os valores ou recalcule a linha-pai.',
      unnamedParent: 'linha-pai',
      unnamedChildren: 'linhas filhas',
      highlightedRows:
        'As linhas destacadas em amarelo estão em conflito hierárquico. Ajuste os valores ou recalcule a linha-pai.',
    },
    hierarchyOffer: {
      title: 'Recalcular linha-pai?',
      description:
        'A linha "{{child}}" elevou a soma das filhas para {{childrenSum}}, acima do limite atual da linha-pai "{{parent}}" ({{parentAmount}}).',
      confirm: 'Recalcular linha-pai',
      dismiss: 'Manter como está',
      applied: 'Linha-pai recalculada com base nas filhas',
    },
    hierarchy: {
      groupToggle: 'Agrupar hierarquia',
      expandAll: 'Expandir tudo',
      collapseAll: 'Recolher tudo',
      childCount: '{{count}} linhas',
      inheritsScope: 'herda escopo',
      dimensionAny: {
        costCenter: 'centro de custo qualquer',
        department: 'departamento qualquer',
        managerialAccount: 'conta gerencial qualquer',
        glAccount: 'conta razão qualquer',
        segment: 'segmento qualquer',
      },
      allocatedSubline: 'Σ',
      allocatedRowTotal: 'Σ filhas',
      syncInlineCta: 'Atualizar teto ({{amount}})',
      syncInlineDismiss: 'Dispensar',
      footerLeafTotal: 'Total folha por período',
      kpiLeafHint: 'Σ alocado nas linhas-folha',
    },
    periodStatus: {
      closed: 'fechado',
      current: 'em curso',
      projected: 'projetado',
    },
    summary: {
      budgeted: 'Orçado no período',
      budgetedHint: '{{periods}} meses · {{lines}} linhas',
      selectedLineHint: '{{periods}} meses · {{line}}',
      realized: 'Realizado',
      realizedHint: 'períodos contabilizados',
      committed: 'Comprometido',
      committedHint: 'AP em aberto + recorrências',
      consumption: 'Consumo projetado',
      consumptionHint: 'real + comprometido / orçado',
      available: 'Saldo livre',
      availableHint: 'ainda não comprometido',
      attentionBadge: '{{count}} linhas',
      attentionHint: 'acima do limite de alerta no mês atual',
      view: 'Ver',
    },
    footerTotals: {
      budgeted: 'Orçado',
      realized: 'Realizado',
      committed: 'Comprometido',
      consumption: 'Consumo real + compr.',
    },
  },
  results: {
    payrollProjection: {
      tooltip:
        'Sinalização consultiva pela projeção de folha do FP&A (regra {{rule}}, cenário {{ruleSetId}}, revisão {{ruleSetRevision}}); não representa caixa comprometido e não há folha processada para {{month}}',
      adHoc: 'Pré-aprovações ad-hoc',
      payroll: 'Folha projetada pelo FP&A',
      codes: {
        BUDGET_PAYROLL_PROJECTION_UNAVAILABLE:
          'Revise o cenário de folha em FP&A → Modelagem → Folha. O padrão da entidade é usado quando não há seleção explícita.',
        BUDGET_PAYROLL_PROJECTION_CONFIDENTIAL:
          'A projeção agregada da folha foi ocultada porque seu acesso a dados de folha é restrito.',
        BUDGET_PAYROLL_PROJECTION_MULTIPLE_RULE_SETS:
          'Há mais de um cenário ativo de FP&A com folha. Designe um único cenário para o orçamento; até lá, a projeção de folha fica indisponível e nenhum valor é publicado.',
        BUDGET_PAYROLL_PROJECTION_CURRENCY_MISMATCH:
          'Ajuste a moeda da projeção de folha do FP&A para a moeda da linha orçamentária (BRL no MVP). O Orçamento não converte câmbio e, até a correção, nenhum valor de folha é publicado.',
        BUDGET_PAYROLL_PROJECTION_RUN_TYPE_UNAVAILABLE:
          'Revise a origem da projeção da folha; o tipo de execução necessário para evitar duplicidade não foi informado e, até a correção, nenhum valor de folha é publicado.',
        UNATTRIBUTABLE:
          'Revise a atribuição contábil da folha no FP&A; parte da projeção não encontrou uma linha do orçamento.',
        FILTER_REFERENCE_UNRESOLVED:
          'Revise os filtros da regra de FP&A; uma referência configurada não existe mais.',
        HEADCOUNT_PLAN_UNATTRIBUTED:
          'Vincule as posições do plano de headcount a contas e dimensões para atribuí-las ao orçamento.',
        ENCARGOS_JURISDICTION_UNSUPPORTED:
          'Configure a política de encargos da jurisdição da entidade antes de usar o plano de headcount.',
        ENCARGOS_POLICY_NOT_EFFECTIVE:
          'Ajuste a vigência da política de encargos para cobrir o período projetado.',
        JOINERS_NOT_MODELLED:
          'Cadastre futuras admissões no plano de headcount; o run-rate não as cria automaticamente.',
        UNRECOGNISED_RUBRIC:
          'Classifique as rubricas de folha não reconhecidas para corrigir a base recorrente.',
        PAYROLL_MANAGERIAL_UNRESOLVED: 'Mapeie a conta contábil da folha para uma conta gerencial.',
        UNMAPPED_COLLABORATOR:
          'Vincule o colaborador PJ a uma pessoa ou fornecedor antes de projetá-lo.',
        PAYROLL_BASE_RUN_STALE:
          'Importe uma folha recente para os tipos habilitados; uma base está ausente ou fora da validade configurada.',
        PAYROLL_TERMINATION_IDENTITY_AMBIGUOUS:
          'Complete a identificação do desligamento para evitar duplicidade na população projetada.',
        PAYROLL_CURRENCY_MISMATCH:
          'Corrija a moeda da folha para a moeda funcional do livro; a projeção não converte valores.',
        PAYROLL_PROVISION_MISSING:
          'Importe ou processe as provisões ausentes dos empregados usados na projeção.',
        INSUFFICIENT_HISTORY:
          'Histórico de RPA insuficiente para a janela e recorrência configuradas.',
        UNAVAILABLE:
          'Folha CLT indisponível. Importe uma folha mensal para projetar este componente.',
        NO_SOURCE_DATA: 'Não há folha importada do tipo habilitado na janela configurada.',
        PAYROLL_EMPLOYEE_SKIPPED:
          'Revise a base de folha: há valores não positivos ou ocorrências insuficientes para a média de RPA.',
        PAYROLL_COLLABORATOR_STALE:
          'Registre uma cobrança recente do colaborador PJ para atualizar o run-rate.',
        PAYROLL_COLLABORATOR_NO_EMISSION:
          'Revise o modelo recorrente do colaborador PJ; ele não gerou meses projetáveis.',
        PAYROLL_REFUSED_COVERED_BY_RESIDUAL:
          'Revise a cobertura da folha recusada; o FP&A a manteve apenas na projeção residual.',
        PAYROLL_TEMPLATE_NOT_MODELLED:
          'Modele o compromisso recorrente de folha no FP&A para projetá-lo explicitamente.',
        PAYROLL_TEMPLATE_WINDOW_DRIFTED:
          'Alinhe a vigência do modelo recorrente de folha com a janela projetada.',
      } satisfies Record<BudgetPayrollProjectionCode, string>,
    },
    buckets: {
      realized: 'Realizado',
      scheduled: 'Agendado',
      committed: 'Comprometido',
      projected: 'Projetado',
      available: 'Disponível',
    },
    bucketDescriptions: {
      realized: 'Pago e contabilizado (GL)',
      scheduled: 'Aprovado e contabilizado (GL), aguardando pagamento',
      committed: 'PO / recorrente / exceção — fonte única',
      projected: 'Pré-aprovação sem PO — não gera commitment',
      available: 'Limite menos utilização',
    },
    totalLine: 'Total linha',
    totalPerPeriod: 'Total folha por período',
    filteredEmpty: {
      title: 'Nenhuma linha atinge o total filtrado',
      description: 'Ajuste ou limpe o filtro de total para ver as linhas do orçamento.',
    },
    empty: {
      title: 'Este orçamento ainda não tem linhas',
      description: 'Edite o orçamento para adicionar linhas e limites.',
    },
    cellDetailHint:
      'Clique numa célula para detalhar realizado, agendado, comprometido e projetado.',
    inheritsScope: '↳ herda escopo — {{label}}',
    legend: {
      withinLimit: 'Dentro do limite',
      forecastBreach: 'Exposição acima do limite',
      realizedBreach: 'Realizado + agendado + comprometido acima do limite',
      nearLimit: 'Limite de alerta',
      overLimit: 'Estourado',
    },
    signals: {
      lineForecastPill: 'Projeção acima',
      lineBreachPill: 'Estouro',
      scopeHint:
        'Sinais na linha refletem o total acumulado; em cada célula, o mesmo critério vale para o período. ▲ = exposição (realizado + agendado + comprometido + projeção ad-hoc) acima do limite; a projeção de folha é apenas consultiva. ● = realizado + agendado + comprometido acima do limite.',
    },
    drawer: {
      limitTitle: '{{amount}} de limite',
      singleCell: 'Célula única · período {{period}}',
      utilized: '{{amount}} utilizado',
      summary: '{{pct}} de {{limit}} · {{available}} disponível',
      availableTitle: 'Disponível no limite',
      budgetSummary: 'de {{limit}} orçado · {{pct}} consumido',
      previousPeriod: 'Período anterior',
      nextPeriod: 'Próximo período',
      thresholdMarker: '{{threshold}}% alerta',
      openApHint: 'AP em aberto',
      searchPlaceholder: 'Buscar fornecedor ou documento',
      eventRange: '{{start}}–{{end}} de {{total}} eventos',
      explainedAbove: '80% do consumo explicado acima',
      remainingEvents: 'demais {{count}} eventos · {{amount}}',
      footerCount: '{{count}} eventos · soma',
      export: 'Exportar',
      exportSuccess: 'Eventos exportados',
      exportError: 'Não foi possível exportar os eventos',
      emptySearch: 'Nenhum evento corresponde à busca',
      inheritsRule: 'Herda a regra do limite total',
      tabs: {
        events: 'Eventos',
        managerialAccounts: 'Por conta gerencial',
        limitRule: 'Regra do limite',
      },
      columns: {
        type: 'Tipo',
        vendorDocument: 'Fornecedor / documento',
        competence: 'Competência',
        status: 'Situação',
        amount: 'Valor',
        limitPct: '% lim.',
      },
      exceptionLimitSummary:
        'Inclui {{exceptionAmount}} de exceção aprovada · limite efetivo {{effectiveLimit}} ({{effectivePct}} c/ projetado)',
      eventTypes: {
        payrollClt: 'Folha CLT',
        payrollRpa: 'Folha RPA',
        payrollProLabore: 'Folha pró-labore',
        plannedClt: 'Posição planejada CLT',
        plannedPj: 'Posição planejada PJ',
        bill: 'Conta a pagar',
        recurringBill: 'Conta recorrente',
        purchaseOrder: 'Pedido de compra',
        commitment: 'Compromisso',
      },
      eventsTitle: 'Eventos que impactam o limite',
      limitMarker: '100%',
      exposurePct: '{{pct}} c/ projetado',
      status: {
        within: 'Dentro do limite',
        warning: 'Atenção · acima de {{threshold}}%',
        over: 'Exposição acima do limite',
        noLimit: 'Sem limite definido',
      },
    },
    loadError: 'Não foi possível carregar os resultados do orçamento.',
    hierarchyUnavailable: {
      title: 'Totais temporariamente indisponíveis',
      description: 'Não foi possível validar toda a hierarquia das linhas do orçamento.',
    },
  },
  tabs: {
    lines: 'Linhas de orçamento',
    costCenters: 'Por centro de custo',
    alerts: 'Alertas',
    settings: 'Configurações',
  },
  actions: {
    newLine: 'Nova linha de orçamento',
    save: 'Salvar',
    saveSettings: 'Salvar configurações',
    requestException: 'Solicitar exceção',
    requestExceptionAgain: 'Solicitar novamente',
    approveException: 'Aprovar exceção',
    rejectException: 'Rejeitar exceção',
    edit: 'Editar',
    editBudget: 'Editar orçamento',
    archive: 'Arquivar',
    dismiss: 'Dispensar',
    cancel: 'Cancelar',
    close: 'Fechar',
    makeActive: 'Tornar ativo',
  },
  fields: {
    entity: 'Entidade',
    book: 'Livro',
    period: 'Período',
    startPeriod: 'Período inicial',
    endPeriod: 'Período final',
    dimensionType: 'Escopo',
    costCenter: 'Centro de custo',
    department: 'Departamento',
    managerialAccount: 'Conta gerencial',
    glAccount: 'Conta contábil',
    glAccountPlaceholder: 'Selecione uma conta contábil',
    customSegment: 'Campo customizado',
    customSegmentPlaceholder: 'Selecione um campo',
    customSegmentValue: 'Valor do campo',
    customSegmentValuePlaceholder: 'Selecione um valor',
    allSegments: 'Todos os campos',
    allSegmentValues: 'Todos os valores',
    amount: 'Limite total',
    amountPlaceholder: 'Ex.: 4200000, 4,2M ou 100k',
    perPeriodAmount: 'Limite por período',
    perPeriodAmountPlaceholder: 'Ex.: 350000 ou 350k',
    perPeriodEnforcementMode: 'Controle por período',
    enforcementMode: 'Controle do limite total',
    alertWarning: 'Alerta',
    alertCritical: 'Alerta crítico',
    designatedBook: 'Livro base do orçamento',
    unmatchedSpendPolicy: 'Política de gasto não mapeado',
    blockEnforcementWhileStale: 'Bloquear controle quando desatualizado',
    justification: 'Justificativa',
  },
  dimensionType: {
    COST_CENTER: 'Centro de custo',
    DEPARTMENT: 'Departamento',
    COMPANY: 'Empresa',
  },
  enforcementMode: {
    HARD_BLOCK: 'Bloqueio total',
    SOFT_BLOCK: 'Bloqueio c/aprovação',
    ALERT_ONLY: 'Alerta apenas',
  },
  status: {
    ACTIVE: 'Ativo',
    INACTIVE: 'Inativo',
    ARCHIVED: 'Arquivado',
    PENDING: 'Pendente',
    APPROVED: 'Aprovado',
    REJECTED: 'Rejeitado',
  },
  recomputeStatus: {
    PENDING: 'Recomputação pendente',
    RUNNING: 'Recomputando utilização',
    COMPLETED: 'Utilização atualizada',
    FAILED: 'Falha na recomputação',
  },
  labels: {
    company: 'Empresa',
    allCategories: 'Todas as categorias',
    allManagerialAccounts: 'Todas as contas gerenciais',
    allGlAccounts: 'Todas as contas contábeis',
    allSegments: 'Todos os campos',
    allSegmentValues: 'Todos os valores',
    fallbackLine: 'Residual (Outros)',
    required: 'Obrigatório',
    optional: 'Opcional',
    withinBudget: 'Dentro do limite',
    withAlert: 'Com alerta',
    warningThreshold: 'Faixa de alerta',
    criticalThreshold: 'Faixa crítica',
    budgetSummary: 'Resumo do orçamento',
    noEntitySelected: 'Selecione uma entidade para carregar o orçamento',
    noLines: 'Nenhuma linha de orçamento cadastrada',
    noAlerts: 'Nenhum alerta ativo',
    loading: 'Carregando...',
    utilization: 'Utilização',
    utilizationFormula: 'Utilização (realizado + comprometido)',
    availableAfterProjected: 'Disponível após projeção',
    available: 'Disponível',
    used: 'Realizado',
    usedPlusAvailable: 'Realizado + disponível',
    subAccounts: '{{count}} sub-contas',
    exceeded: 'Excedido',
    exceededBy: 'Excedido em',
    currentException: 'Exceção atual',
    softBlockTitle: 'Orçamento excedido',
    exceptionApprovedTitle: 'Exceção aprovada',
    exceptionRejectedTitle: 'Exceção rejeitada',
    hardBlockTitle: 'Orçamento excedido — envio bloqueado',
    hardBlockDescription:
      'Esta conta foi rejeitada automaticamente porque excede o limite rígido do orçamento. Exceções não são permitidas neste caso. Ajuste a conta ou atualize a linha de orçamento e envie novamente para aprovação.',
    hardBlockProactiveDescription:
      'Esta conta excede o limite rígido do orçamento. O envio para aprovação será bloqueado e exceções não são permitidas. Ajuste a conta ou atualize a linha de orçamento antes de enviar.',
    hardBlockActions:
      'Reduza o valor, altere as dimensões de classificação ou aumente o limite da linha no orçamento.',
    projectedExceedTitle: 'Projeção excede o orçamento',
    projectedExceedDescription:
      'Somando realizado, comprometido e contas ad-hoc em aprovação, a linha ficaria acima do limite. Revise antes de enviar — isso não bloqueia o envio.',
    softBlockDescription:
      'Esta conta excede o orçamento configurado e precisa de uma exceção aprovada para seguir até a aprovação final.',
    exceptionPending: 'Exceção de orçamento solicitada. Aguardando aprovação.',
    exceptionRejected: 'Solicitação rejeitada. Solicite novamente para continuar.',
    exceptionApprovedFinalize:
      'Exceção de orçamento aprovada. Você já pode concluir a aprovação desta conta.',
    exceptionAppliedSummary:
      'Exceção aprovada adicionou {{amount}} ao limite da linha (limite efetivo {{effectiveLimit}}).',
    exceptionAppliedLabel: 'com exceção aprovada',
    budgetLine: 'Linha de orçamento',
    assessedPeriod: 'Período avaliado',
    lineLimitSuffix: 'de limite',
    exceededTotalLimit: 'Limite total excedido em {{amount}}',
    exceededPeriodLimit: 'Limite por período excedido em {{amount}}',
    scopeConfiguration: 'Configuração do escopo selecionado',
    excess: 'Excedente',
    limitLegend: 'Limite ({{amount}})',
    alertScopeTotal: 'Aplica-se à utilização acumulada do limite total.',
    alertScopePeriod: 'Aplica-se à utilização do limite por período.',
    inheritsTotalControl: 'Usa a mesma regra definida para o limite total.',
  },
  alerts: {
    draftTitle: 'Orçamento Rascunho',
    draftDescription:
      'Você pode visualizar os limites e utlização com base no rascunho, mas será necessário ativar o orçamento para que ele seja aplicado em outros módulos.',
    reforecastDraftDescription:
      'Este reforecast ainda não é o orçamento que limita os gastos. O plano em vigor é {{enforcing}}.',
    enforcingTitle: 'Este orçamento está em vigor',
    enforcingDescription:
      'Promovido do reforecast {{revision}}, que substituiu {{previous}}. Aprovado por {{approver}} em {{date}}.',
    supersededTitle: 'Orçamento substituído',
    supersededDescription:
      'Este plano foi substituído por um reforecast aprovado. O consumo passou a ser apontado no novo orçamento em vigor. Este plano permanece visível, mas a utilização atual é do plano que o substituiu e ele não limita mais os gastos.',
    statusWarning: 'Esse orçamento está em status {{status}}',
    statusWarningDescription: 'Ative o orçamento para liberar as ações principais.',
    payrollDataRestricted: 'Atenção: valores de folha não estão sendo exibidos.',
    recomputeStaleDescription:
      'A utilização ainda está sendo recomputada em segundo plano. Alguns resultados de controle podem ficar temporariamente bloqueados.',
    recomputeWarningTitle: 'Recomputação concluída com alertas',
    recomputeWarningDescription:
      'Alguns modelos recorrentes foram ignorados porque a classificação não tem rateio de conta contábil. Corrija a classificação deles para incluí-los no comprometido.',
    showDetails_one: 'Ver {{count}} modelo',
    showDetails_other: 'Ver {{count}} modelos',
    hideDetails: 'Ocultar',
  },
  tooltips: {
    dimensionType:
      'Define se esta linha controla orçamento por centro de custo, departamento ou empresa inteira.',
    managerialAccount:
      'Conta gerencial coberta pela linha. Use para restringir o orçamento a uma categoria contábil específica.',
    glAccount:
      'Conta contábil coberta pela linha. Use junto com a conta gerencial para restringir ainda mais o orçamento.',
    customSegment: 'Campo customizado usado como filtro adicional da linha de orçamento.',
    customSegmentValue: 'Valor específico do campo customizado coberto por esta linha.',
    costCenter:
      'Centro de custo que consumirá este orçamento quando o escopo selecionado for centro de custo.',
    department:
      'Departamento que consumirá este orçamento quando o escopo selecionado for departamento.',
    startPeriod: 'Primeiro período contábil em que a linha de orçamento fica vigente.',
    endPeriod: 'Último período contábil coberto pela linha de orçamento.',
    amount:
      'Valor máximo acumulado permitido para todo o intervalo selecionado. Atalhos: 100k, 1M, 10M, 1B.',
    perPeriodAmount:
      'Valor máximo permitido em cada período. Atalhos: 100k, 1M, 10M, 1B. Deixe em branco para controlar apenas pelo limite total.',
    enforcementMode: 'Regra aplicada quando o consumo ultrapassa o limite total configurado.',
    perPeriodEnforcementMode:
      'Regra aplicada quando o consumo ultrapassa o limite de um período. Sem seleção, herda o controle do limite total.',
    alertWarning:
      'Percentual de utilização que dispara o primeiro alerta antes do bloqueio ou exceção.',
    alertCritical: 'Percentual de utilização considerado crítico e que exige atenção imediata.',
  },
  table: {
    dimension: 'Dimensão',
    scope: 'Escopo',
    coverage: 'Cobertura',
    limit: 'Limite',
    perPeriodLimit: 'Limite / período',
    utilized: 'Realizado',
    available: 'Disponível',
    utilization: 'Utilização',
    mode: 'Modo',
  },
  detail: {
    commitmentsTitle: 'Compromissos',
    billsTitle: 'Contas realizadas',
    eventsEmpty: 'Nenhum evento neste período.',
    commitmentsEmpty: 'Nenhum compromisso aberto neste período.',
    billsEmpty: 'Nenhuma conta lançada neste período.',
    loadError: 'Não foi possível carregar os detalhes da linha.',
    eventsPagination:
      'Mostrando {{shown}} de {{total}} eventos (página {{page}} de {{totalPages}})',
    eventsPrevPage: 'Anterior',
    eventsNextPage: 'Próxima',
    columns: {
      source: 'Origem',
      reference: 'Referência',
      period: 'Período',
      openAmount: 'Valor aberto',
      status: 'Status',
      vendor: 'Fornecedor',
      description: 'Descrição',
      postedAmount: 'Valor realizado',
      date: 'Data',
      bucket: 'Bucket',
    },
    sort: {
      dateDesc: 'Data mais recente',
      amountDesc: 'Maior valor',
      amountAsc: 'Menor valor',
    },
    commitmentSource: {
      PURCHASE_ORDER: 'Pedido de compra',
      RECURRING_BILL: 'Conta recorrente',
      BUDGET_EXCEPTION: 'Exceção de orçamento',
    },
    commitmentStatus: {
      OPEN: 'Aberto',
      PARTIALLY_SETTLED: 'Parcialmente liquidado',
      SETTLED: 'Liquidado',
      CANCELLED: 'Cancelado',
    },
    exceptionCommitmentTag: 'Exceção aprovada',
    exceptionCommitmentReference: 'Conta da exceção:',
  },
  filters: {
    all: 'Todas',
    alert: 'Com alerta',
    within: 'Dentro do limite',
  },
  tone: {
    critical: 'Atenção necessária',
    warning: 'Alerta',
    normal: 'Normal',
  },
  insights: {
    title: 'Inteligência de Orçamento',
    healthy: 'Nenhum centro de custo está acima dos limites configurados no período selecionado.',
    alertLine:
      '{{line}} está em {{utilization}} de utilização; saldo restante: {{available}}. Revise alertas e exceções antes do fechamento.',
    viewAlerts: 'Ver alertas',
  },
  metrics: {
    totalBudget: 'Orçamento total',
    realized: 'Realizado',
    available: 'Disponível',
    alertLines: 'Linhas com alerta',
    activeLines: '{{count}} linhas ativas',
    utilizationShare: '{{percent}} do orçamento',
    remaining: 'Saldo restante',
    criticalLines: '{{count}} críticas',
  },
  sections: {
    linesTitle: 'Linhas de orçamento',
    linesDescription: 'Orçamento por dimensão. Clique em uma linha para detalhar.',
    costCentersTitle: 'Por centro de custo',
    costCentersDescription: 'Visão consolidada de realizado e saldo por centro de custo.',
    alertsTitle: 'Alertas ativos',
    alertsDescription: 'Exceções, bloqueios e previsões de estouro.',
  },
  dialog: {
    createTitle: 'Nova linha de orçamento',
    editTitle: 'Editar linha de orçamento',
    description: 'Configure o limite, o período coberto e as regras de controle.',
    editBudgetTitle: 'Editar orçamento',
    editBudgetDescription: 'Atualize nome, descrição e vigência do orçamento.',
    deleteLineTitle: 'Excluir linha de orçamento?',
    deleteLineDescription: 'A linha "{{name}}" será arquivada e deixará de afetar o orçamento.',
    hierarchyHint:
      'Linhas mais específicas consomem o orçamento de linhas-pai na mesma dimensão. A soma dos filhos não pode exceder o limite da linha-pai. Gastos sem correspondência vão para a linha Outros.',
    exceptionTitle: 'Solicitar exceção de orçamento',
    exceptionDescription: 'Explique por que a conta deve seguir mesmo acima do limite configurado.',
    approveExceptionTitle: 'Aprovar exceção de orçamento',
    rejectExceptionConfirm: 'Rejeitar esta exceção de orçamento?',
    rejectExceptionDescription:
      'A solicitação será rejeitada e não poderá mais ser aprovada. Confirme para continuar.',
    payable: 'Conta a pagar',
    value: 'Valor',
    costCenterCategory: 'CC / Categoria',
    exceptionImpact:
      'A aprovação desta exceção registrará {{amount}} de excesso no histórico do orçamento.',
    resolutionPlaceholder: 'Ex.: campanha adicional aprovada pela diretoria...',
  },
  settings: {
    title: 'Padrões globais',
    description: 'Defina o livro base e os thresholds padrão aplicados às novas linhas.',
    budgetBookTitle: 'Livro base do orçamento',
    budgetBookDescription: 'Livro usado como referência para novas linhas.',
    enforcementTitle: 'Controle padrão do orçamento',
    enforcementDescription: 'O que acontece quando uma conta a pagar excede o orçamento.',
    thresholdTitle: 'Threshold de alerta antecipado',
    thresholdDescription: 'Dispare alerta quando a utilização atingir este percentual.',
    rolloverTitle: 'Rollover de saldo não utilizado',
    rolloverDescription: 'O que acontece com saldo restante no fim do período.',
    rolloverCurrent: 'Transferir para próximo período. Saldo acumula no período seguinte.',
  },
  enforcementModeDescription: {
    HARD_BLOCK: 'Bloqueia o lançamento/aprovação quando o limite é ultrapassado.',
    SOFT_BLOCK: 'Permite seguir somente com exceção ou aprovação especial.',
    ALERT_ONLY: 'Não bloqueia o fluxo; apenas sinaliza e notifica responsáveis.',
  },
  hints: {
    monthlyCoverage: 'Mensal',
    rangedCoverage: 'Multi-período',
  },
  messages: {
    settingsSaved: 'Configurações de orçamento salvas com sucesso',
    budgetCreated: 'Orçamento criado com sucesso',
    budgetDuplicated: 'Orçamento duplicado com sucesso',
    budgetActivated: 'Orçamento ativado com sucesso',
    budgetDeactivated: 'Orçamento inativado com sucesso',
    budgetUtilizationRecomputePending:
      'A utilização do orçamento será recalculada em segundo plano. Os valores podem levar alguns minutos para atualizar.',
    budgetArchived: 'Orçamento arquivado com sucesso',
    budgetUpdated: 'Orçamento atualizado com sucesso',
    bulkUpdated: 'Linhas atualizadas com sucesso',
    bulkArchived: 'Linhas arquivadas com sucesso',
    lineSaved: 'Linha de orçamento salva com sucesso',
    lineArchived: 'Linha de orçamento arquivada com sucesso',
    exceptionRequested: 'Exceção solicitada',
    exceptionResolved: 'Exceção atualizada com sucesso',
    exceptionRejected: 'Exceção rejeitada com sucesso',
    loadError: 'Não foi possível carregar os dados de orçamento',
    saveError: 'Não foi possível salvar os dados de orçamento',
    linePeriodInvalid:
      'O período inicial/final escolhido não está disponível para esta entidade/livro. Verifique os períodos e tente novamente.',
    reviewPeriods: 'Verificar períodos',
    openChartOfAccounts: 'Abrir plano de contas',
    invalidGlAccount: {
      generic:
        'A conta contábil selecionada não é válida para esta entidade e livro. Escolha outra conta ou revise a configuração contábil.',
      coaVersionNotAssigned:
        'Não há um plano de contas ativo vinculado a esta entidade e livro. Ative o plano de contas no módulo contábil e tente salvar novamente.',
      accountNotFound:
        'A conta contábil selecionada não foi encontrada. Atualize a página, selecione outra conta e tente novamente.',
      accountNotPostable:
        'A conta selecionada não permite lançamentos. Escolha uma conta analítica (postável) na lista.',
      accountInactive:
        'A conta selecionada está inativa. Escolha uma conta ativa ou reative-a no plano de contas.',
      versionMismatch:
        'A conta pertence a uma versão diferente do plano de contas ativo para esta entidade/livro. Selecione uma conta da versão ativa ou atualize a ativação contábil.',
    },
    activateZeroAmountLines:
      'Não é possível ativar o orçamento: todas as linhas ativas precisam ter limite total maior que zero.',
    revisionNotActivatable:
      'Um reforecast não pode ser ativado diretamente. Use a aprovação para torná-lo o orçamento em vigor.',
    reforecastPromoted: 'Reforecast aprovado e agora em vigor.',
    reforecastCreated:
      'Reforecast criado. Edite os valores e aprove para trocar o orçamento em vigor.',
    reforecastDefaultName: 'Reforecast {{start}}–{{end}}',
    reforecastCreateTitle: 'Criar reforecast',
    reforecastCreateDescription:
      'Cria uma cópia em rascunho do orçamento em vigor, com os limites atuais. Depois você edita e aprova para trocar o plano que limita os gastos.',
    reforecastCreateAmountsHint:
      'Os valores de cada período começam iguais aos do orçamento em vigor. Você pode editá-los depois.',
    reforecastCreatePartialWarning:
      'Esta janela não cobre todos os períodos do orçamento em vigor. Você poderá editar o reforecast, mas a aprovação será recusada até a cobertura ficar completa.',
    reforecastPromoteTitle: 'Aprovar reforecast e trocar o orçamento em vigor',
    reforecastPromoteDescription:
      'O plano {{current}} deixará de limitar os gastos. O reforecast {{revision}} passará a ser o orçamento em vigor.',
    reforecastPromoteConfirm: 'Aprovar e trocar',
    reforecastPromoteCancel: 'Cancelar',
    reforecastReviewAgain: 'Revisar prévia atualizada',
    reforecastPreviewRequired: 'Carregue e revise a prévia antes de aprovar.',
    reforecastPeriod: 'Período',
    reforecastPreviousAmount: 'Limite atual',
    reforecastNextAmount: 'Novo limite',
    reforecastSelfApproval:
      'A aprovação exige um segundo usuário. Qualquer autor de alterações materiais neste reforecast não pode promovê-lo.',
    reforecastPreparerRequired:
      'Grave uma alteração de linha ou valor neste reforecast antes de aprovar. Esta cópia não tem preparador registrado.',
    reforecastPartialCoverage:
      'Este reforecast não cobre todos os períodos do orçamento em vigor. Períodos faltantes: {{periods}}.',
    validation: {
      budgetBookRequired: 'Selecione um livro contábil para criar o orçamento.',
      budgetBookNotLinked:
        'O livro contábil designado não está vinculado a esta entidade. Revise as configurações de orçamento.',
      effectiveFromInvalid: 'A data inicial de vigência é inválida.',
      effectiveUntilInvalid: 'A data final de vigência é inválida.',
      effectiveRangeInvalid: 'A data inicial não pode ser posterior à data final.',
      effectiveUntilRequired: 'Informe a data final de vigência.',
      endPeriodRequired: 'Informe o período final para orçamentos com template.',
      archivedBudget: 'Orçamentos arquivados não podem ser editados.',
      archivedBudgetActivate: 'Orçamentos arquivados não podem ser ativados.',
      supersededNotActivatable: 'Orçamentos substituídos não podem ser ativados.',
      supersededImmutable: 'Orçamentos substituídos não podem ser alterados.',
      reforecastPartialCoverage:
        'Este reforecast não cobre todos os períodos do orçamento em vigor. Períodos faltantes: {{periods}}.',
      reforecastNotPromotable:
        'Só um reforecast em rascunho da mesma família de versões pode ser promovido.',
      reforecastNoActive:
        'Não há orçamento ativo nesta entidade e livro para o reforecast substituir.',
      reforecastPromotionConflict:
        'Os valores do reforecast ou o orçamento em vigor mudaram. Revise a prévia atualizada antes de aprovar.',
      reforecastSelfApproval:
        'A aprovação exige um segundo usuário. Qualquer autor de alterações materiais neste reforecast não pode promovê-lo.',
      reforecastPreparerRequired:
        'Grave uma alteração de linha ou valor neste reforecast antes de aprovar. Esta cópia não tem preparador registrado.',
      reforecastEnforcingCannotDeactivate:
        'Um reforecast em vigor não pode ser inativado. Crie outro reforecast e aprove-o para trocar o orçamento que limita os gastos.',
      deactivateBeforeArchive: 'Desative o orçamento antes de arquivá-lo.',
      unsupportedTemplateScope: 'O template selecionado não é suportado.',
      perPeriodEnforcementRequiresLimit:
        'Defina um limite por período antes de alterar o modo de enforcement por período.',
      alertThresholdWarningRange: 'O alerta de aviso deve estar entre 0% e 100%.',
      alertThresholdCriticalRange: 'O alerta crítico deve estar entre 0% e 100%.',
      alertThresholdOrder: 'O alerta de aviso deve ser menor ou igual ao alerta crítico.',
      noPeriodsInEffectiveRange:
        'Não há períodos contábeis na vigência selecionada. Crie ou abra os períodos e tente novamente.',
      contiguousEffectivePeriods:
        'A vigência do orçamento deve cobrir períodos contíguos sem lacunas.',
      noActiveCostCenters:
        'Não há centros de custo ativos para a entidade selecionada. Cadastre centros de custo antes de usar este template.',
      noActiveDepartments:
        'Não há departamentos ativos para a entidade selecionada. Cadastre departamentos antes de usar este template.',
      noActiveManagerialAccounts:
        'Não há contas gerenciais de despesa ativas para a entidade selecionada. Cadastre o plano de contas gerencial antes de usar este template.',
      fallbackLineScopeLocked: 'A linha Outros não permite alteração de escopo.',
      costCenterDimensionInvalid:
        'Linhas por centro de custo exigem um centro de custo e não podem ter departamento.',
      departmentDimensionInvalid:
        'Linhas por departamento exigem um departamento e não podem ter centro de custo.',
      companyDimensionInvalid: 'Linhas da empresa não podem ter centro de custo nem departamento.',
      amountNonNegative: 'O valor do orçamento deve ser zero ou maior.',
      amountPositive: 'O limite total deve ser maior que zero.',
      amountInvalidScale: 'O valor deve ter no máximo duas casas decimais.',
      perPeriodAmountPositive: 'O limite por período deve ser maior que zero.',
      periodLimitsMissingPeriodId: 'Cada limite por período precisa de um período válido.',
      periodLimitsDuplicatePeriodId: 'Há períodos duplicados nos limites por período.',
      periodLimitsInvalidAmount:
        'Cada limite por período precisa ser um valor numérico válido com no máximo duas casas decimais.',
      periodLimitsNegativeAmount: 'Os limites por período devem ser zero ou maiores.',
      periodLimitsOutsideWindow:
        'Um ou mais limites por período estão fora da janela da linha. Revise os períodos.',
      budgetIdRequired: 'O orçamento da linha é obrigatório.',
      archivedBudgetLines: 'Não é possível alterar linhas de um orçamento arquivado.',
      lineEntityBookMismatch: 'A entidade/livro da linha deve coincidir com o orçamento.',
      invalidCostCenter: 'Centro de custo inválido para a entidade selecionada.',
      invalidDepartment: 'Departamento inválido para a entidade selecionada.',
      costCenterInactive:
        'O centro de custo selecionado está inativo. Escolha um centro de custo ativo.',
      departmentInactive: 'O departamento selecionado está inativo. Escolha um departamento ativo.',
      invalidManagerialAccount: 'Conta gerencial inválida para a entidade selecionada.',
      segmentValueRequiresSegment: 'Selecione o segmento antes de escolher o valor.',
      invalidCustomSegment: 'Segmento personalizado inválido para a entidade selecionada.',
      invalidCustomSegmentValue: 'Valor de segmento inválido para o segmento selecionado.',
      segmentHasTextValues:
        'O segmento {{segmentName}} já possui contas classificadas com valores digitados. Reclassifique essas contas antes de orçá-lo.',
      startAfterEndPeriod: 'O período inicial não pode ser posterior ao período final.',
      noPeriodsInLineRange: 'Não há períodos contábeis na faixa selecionada para esta linha.',
      contiguousLinePeriods: 'A faixa de períodos da linha deve ser contígua.',
      totalBelowPeriodLimits:
        'O limite total não pode ser menor que a soma dos limites por período.',
      stampedPeriodConflict:
        'Esta alteração derrubaria um período carimbado por derivação. Envie dropStampedPeriodIds com cada período a remover.',
      totalLimitCannotBeCleared: 'O limite total não pode ser removido.',
      fallbackLineCannotArchive: 'A linha Outros não pode ser arquivada.',
      selectLinesToUpdate: 'Selecione ao menos uma linha para atualizar.',
      selectLinesToArchive: 'Selecione ao menos uma linha para arquivar.',
      chooseFieldsToUpdate: 'Escolha ao menos um campo para atualizar.',
      archivedBudgetLine: 'Linhas arquivadas não podem ser alteradas.',
      currencyBrlOnly: 'No momento, as linhas de orçamento devem usar BRL.',
      resyncRequiresActive: 'Somente um orçamento ativo pode ser resincronizado.',
      resyncRequiresDesignatedBook:
        'Este orçamento não usa o livro definido para controle orçamentário da entidade.',
      recurringBlockedWhileStale:
        'O orçamento está com o recálculo de utilização pendente. Aguarde a atualização (ou execute a reconciliação do orçamento) e tente salvar a recorrência novamente.',
      ownerMissingPermission:
        'O usuário selecionado não possui permissão para gerenciar linhas de orçamento.',
      ownerTransferForbidden: 'Somente o dono atual do orçamento pode transferir a titularidade.',
      lineOwnerModifyForbidden:
        'Somente o dono do orçamento ou um responsável autorizado pode alterar os limites desta linha.',
      lineOwnerLimitExceeded:
        'O valor informado excede o limite máximo permitido para este responsável.',
      targetOwnerInvalid: 'O novo responsável não foi encontrado ou está inativo.',
      bulkAmountCannotBeCleared: 'Este valor não pode ser removido nesta alteração em massa.',
      bulkAmountInvalidNumber: 'Informe um número válido para esta alteração em massa.',
      bulkAmountMustBePositive: 'O valor da alteração em massa deve ser positivo.',
      bulkAmountUnsupportedOperation: 'Operação de alteração em massa não suportada.',
      bulkAmountResultNotPositive:
        'O resultado da alteração em massa deve ser maior que zero. Revise os valores selecionados.',
      exceptionTargetRequired:
        'Informe exatamente um documento (fatura, requisição, pedido de compra ou recorrência) para a exceção.',
      exceptionNotBlocked: 'Este documento não está bloqueado por um orçamento que exija exceção.',
      exceptionNoExcess: 'Não foi encontrado excesso de orçamento para a linha selecionada.',
      exceptionRecurringRequiresLimitIncrease:
        'Solicitações de orçamento recorrente a partir de um bloqueio rígido devem usar o tipo Aumento de limite.',
      exceptionAlreadyResolved: 'Esta exceção de orçamento já foi resolvida.',
      exceptionRecurringLimitIncreaseOnly:
        'Solicitações de bloqueio rígido recorrente só podem ser aprovadas como Aumento de limite.',
      ambiguousOverlappingRange:
        'Faixas de orçamento sobrepostas com a mesma especificidade devem estar totalmente contidas uma na outra ou ser disjuntas.',
      recurringTemplateHardBlock:
        'Este modelo de conta recorrente excederia o limite de orçamento configurado. Reduza o limite por período, edite as linhas de orçamento ou altere as classificações antes de salvar.',
      exceptionBillHasPendingRequest:
        'Esta fatura já possui uma solicitação de exceção de orçamento pendente. Resolva-a antes de solicitar outra.',
    },
    activationSwitchTitle: 'Substituir orçamento ativo',
    activationSwitchDescription:
      'Já existe um orçamento ativo para esta entidade e livro. Ao continuar, o orçamento atual voltará para rascunho e apenas um plano ficará ativo neste livro.',
    activationSwitchConfirm: 'Substituir orçamento ativo',
    activationSwitchCancel: 'Cancelar',
    activationSwitchBookLabel: 'Livro',
  },
} as const;

export const budgetEn = {
  title: 'Budget',
  list: {
    title: 'Budgets',
    newBudget: 'New budget',
    empty: 'No budgets found',
    count: '{{count}} budgets',
    statusFilter: 'Status',
    createTitle: 'New budget',
    createDescription: 'Create a draft budget plan for the selected entity.',
    duplicateTitle: 'Duplicate budget',
    duplicateDescription: 'A draft copy will be created with the same lines.',
    columns: {
      name: 'Name',
      status: 'Status',
      effective: 'Effective window',
      totalLimit: 'Total limit',
      lineCount: 'Lines',
      active: 'Active',
      actions: 'Actions',
    },
    actions: {
      open: 'Open',
      activate: 'Activate',
      promoteReforecast: 'Approve reforecast',
      createReforecast: 'Create reforecast',
      duplicate: 'Duplicate',
      archive: 'Archive',
    },
    fields: {
      name: 'Name',
      description: 'Description',
      startPeriod: 'Start period',
      endPeriod: 'End period',
      openEndedEnd: 'No end period (open-ended)',
      effectiveFrom: 'Effective from',
      effectiveUntil: 'Effective until',
    },
    emptyState: {
      title: 'Start controlling your spending',
      description:
        'Create a budget to set spending limits by cost center, department, or category. Alchemy tracks actuals in real time and alerts your team before limits are exceeded.',
      createFirst: 'Create first budget',
      howItWorks: 'How it works',
      howItWorksTitle: 'How budget control works',
      howItWorksDescription:
        'Budgets help finance teams set limits and monitor spending before it goes over budget.',
      howItWorksSteps: {
        create: 'Create a budget plan for an entity and GL book.',
        lines: 'Add lines by cost center, department, or managerial account.',
        track: 'Alchemy compares actual and committed amounts against configured limits.',
        enforce: 'Alerts and blocks can be configured per line or globally.',
      },
      templatesHeading: 'Or start from a template',
      templates: {
        cost_center: {
          title: 'By cost center',
          description: 'An annual limit for each company cost center.',
        },
        department: {
          title: 'By department',
          description: 'Distribute the budget across organization areas.',
        },
        managerial_account: {
          title: 'By managerial chart of accounts',
          description: 'One line per expense managerial account, following the chart hierarchy.',
        },
      },
      guidePrefix: 'New to budget control?',
      guideLink: 'See the quick guide',
    },
    createTemplate: {
      heading: 'Line template',
      description: 'Optional. Generate lines automatically from the entity structure.',
      options: {
        none: {
          title: 'Blank',
          description: 'Create the plan without lines and add them manually later.',
        },
        cost_center: {
          title: 'By cost center',
          description: 'One line for each active cost center in the entity.',
        },
        department: {
          title: 'By department',
          description: 'One line for each active department in the entity.',
        },
        managerial_account: {
          title: 'By managerial chart of accounts',
          description: 'One line per expense managerial account, following the chart hierarchy.',
        },
      },
    },
    templateCreate: {
      cost_center: {
        title: 'Lines by cost center',
        description:
          'When saved, the budget will be created with one line for each active cost center in the entity. Limits are set on the detail page.',
      },
      department: {
        title: 'Lines by department',
        description:
          'When saved, the budget will be created with one line for each active department in the entity. Limits are set on the detail page.',
      },
      managerial_account: {
        title: 'Lines by managerial chart of accounts',
        description:
          'When saved, the budget will be created with one line for each active expense managerial account in the entity, following the chart hierarchy. Limits are set on the detail page.',
      },
    },
    validation: {
      required: 'Required field',
      completeRequired: 'Fill all required fields',
    },
    filteredEmpty: 'No budgets found for the selected filters',
  },
  planStatus: {
    DRAFT: 'Draft',
    ACTIVE: 'Active',
    ARCHIVED: 'Archived',
    SUPERSEDED: 'Superseded',
  },
  bulk: {
    selectAll: 'Select all visible lines',
    selectLine: 'Select budget line',
    clearSelection: 'Clear selection',
    selected: '{{count}} selected lines',
    selectedLimit: 'Selected limit',
    actions: {
      totalLimit: 'Total limit',
      periodLimit: 'Period limit',
      totalMode: 'Total mode',
      periodMode: 'Period mode',
      menu: 'Bulk actions',
      archive: 'Delete lines',
      applying: 'Applying...',
      confirm: 'Confirm changes',
    },
    fields: {
      operation: 'Operation',
      value: 'Value',
      emptyClears: 'Leave empty to clear limit',
    },
    operation: {
      SET: 'Set exact value',
      INCREASE_AMOUNT: 'Increase amount',
      DECREASE_AMOUNT: 'Decrease amount',
      INCREASE_PERCENT: 'Increase percent',
      DECREASE_PERCENT: 'Decrease percent',
    },
    dialog: {
      archive: {
        title: 'Delete selected lines?',
        description: '{{count}} lines will be archived and stop affecting the budget.',
      },
      amount: {
        title: 'Change total limit?',
        description: 'This change will be applied to {{count}} selected lines.',
      },
      perPeriodAmount: {
        title: 'Change per-period limit?',
        description: 'This change will be applied to {{count}} selected lines.',
      },
      enforcementMode: {
        title: 'Change total-limit control?',
        description: 'This change will be applied to {{count}} selected lines.',
      },
      perPeriodEnforcementMode: {
        title: 'Change per-period control?',
        description: 'This change will be applied to {{count}} selected lines.',
      },
    },
  },
  history: {
    title: 'History',
    description: 'See who changed the budget and its lines.',
    empty: 'No changes recorded yet.',
    entity: {
      BUDGET: 'Budget',
      BUDGET_LINE: 'Line',
    },
    action: {
      CREATE: 'Created',
      UPDATE: 'Updated',
      ARCHIVE: 'Archived',
      ACTIVATE: 'Activated',
      DUPLICATE: 'Duplicated',
      CREATE_REVISION: 'Created reforecast',
      PROMOTE_REVISION: 'Promoted reforecast',
      BULK_UPDATE: 'Bulk updated',
      BULK_ARCHIVE: 'Bulk archived',
    },
    field: {
      name: 'Name',
      description: 'Description',
      status: 'Status',
      amount: 'Total limit',
      perPeriodAmount: 'Per-period limit',
      enforcementMode: 'Total-limit control',
      perPeriodEnforcementMode: 'Per-period control',
      glAccountId: 'GL account',
      effectiveFrom: 'Effective from',
      effectiveUntil: 'Effective until',
      sourceBudgetId: 'Source',
      updatedByUserId: 'Updated by',
    },
  },
  subtitle: 'Track limits, utilization, alerts, and exceptions by dimension',
  breadcrumbs: {
    accounting: 'Accounting',
    fpa: 'FP&A',
    budget: 'Budget',
  },
  spreadsheet: {
    title: 'Budget spreadsheet',
    backToDetails: 'Back to details',
    anyDimension: '— (any) —',
    addRow: 'Add row',
    addRows: '+{{count}} rows',
    deleteRow: 'Remove row',
    fillRight: 'Replicate right',
    undo: 'Undo',
    redo: 'Redo',
    undoHint: 'Undo (Ctrl+Z)',
    redoHint: 'Redo (Ctrl+Shift+Z)',
    clearValues: 'Clear values',
    periodTotals: 'Total by period',
    navigationHint: 'navigate ·',
    rowSelectionHint: 'click # to select row ·',
    pasteHint: 'paste from Excel',
    segmentColumnDescription: 'Entity custom field',
    status: {
      dirty: 'Unsaved changes',
      saved: 'All saved',
    },
    toolbar: {
      columns: 'Columns',
      filters: 'Filters',
      dimensionColumns: 'Row dimensions',
      rowDimensions: 'Row dimensions',
      filterDimensions: 'Filter dimensions',
      filterPlaceholder: 'Search by cost center, department, account…',
      moveUp: 'Move column up',
      moveDown: 'Move column down',
      columnHint: 'Show, hide, and reorder dimension columns.',
      statusSelector: 'Status ({{count}})',
    },
    alerts: {
      toolbarButton: 'Alerts & requests ({{count}})',
      panelTitle: 'Alerts and requests',
      panelDescription: 'Pending exceptions and alerted lines in this spreadsheet.',
      inlineRequestedAt: 'Exception requested on {{date}}',
      requestedBy: 'Requested by {{name}}',
      amountRequested: 'Excess: {{amount}}',
      sourceBill: 'Bill',
      sourcePurchaseRequest: 'Purchase request',
      sourcePurchaseOrder: 'Purchase order',
      sourceRecurringBill: 'Recurring bill',
    },
    tags: {
      gl: 'GL',
      mng: 'Managerial',
      segment: 'Field',
    },
    columns: {
      rowTotal: 'Row total',
      enforcement: 'Enforcement',
      totalBudget: 'Period total',
      totalBudgetHint: 'real + comm.',
      available: 'Balance',
      availableHint: 'free',
    },
    dimensionDescriptions: {
      costCenter: 'Marketing, IT, Operations…',
      department: 'Organizational hierarchy',
      managerialAccount: 'Managerial category',
      glAccount: 'Chart of accounts (GL)',
    },
    kpis: {
      totalBudgeted: 'Total budgeted',
      lineCount: '{{count}} lines',
      filledLines: 'Filled lines',
      filledLinesHint: 'with at least one value',
      largestLine: 'Largest line',
      averagePerPeriod: 'Average / period',
      periodCount: 'across {{count}} periods',
    },
    howItWorks: {
      title: 'How it works',
      description:
        'Each row is the intersection of the filled dimensions — leave a dimension empty to avoid restricting by it. More specific lines nest under broader parent lines: the parent sets the ceiling and children allocate within it. Use hierarchy grouping to see Σ children vs ceiling and sync when needed.',
    },
    footer: {
      summary: '{{count}} lines · Total budgeted {{total}}',
    },
    actions: {
      saveDraft: 'Save draft',
      activateBudget: 'Activate budget',
      promoteReforecast: 'Approve reforecast',
      createReforecast: 'Create reforecast',
      promotingReforecast: 'Approving reforecast…',
      inactivateBudget: 'Inactivate budget',
      saving: 'Saving…',
      activating: 'Activating…',
      inactivatingBudget: 'Inactivating…',
      resync: 'Resync',
      resyncing: 'Resyncing…',
      openSpreadsheet: 'Spreadsheet mode',
      lineOwners: 'Budget line owners',
      fullscreen: 'Full screen',
      exitFullscreen: 'Exit full screen',
      alertConfig: 'Alert settings',
      editValues: 'Edit values',
      discardChanges: 'Discard changes',
    },
    resync: {
      title: 'Resync commitments?',
      description:
        'Processing will run in the background. Purchase-order and recurring commitments will be rebuilt, realized bills settled, and stale duplicates cancelled before totals are recalculated. Budget limits will not change.',
      success: 'Resync started. The figures will update automatically when processing finishes.',
    },
    lineOwners: {
      title: 'Budget line owners',
      description:
        'Assign line owners to receive budget alerts. The budget owner can allow limit changes.',
      budgetOwner: 'Budget owner',
      owner: 'Owner',
      lineLimit: 'Line limit: {{amount}}',
      canModifyLimits: 'Can modify limits',
      maxLimit: 'Maximum allowed limit',
      actions: {
        save: 'Save',
        saving: 'Saving…',
        cancel: 'Cancel',
      },
      messages: {
        saved: 'Owners updated',
        saveFailed: 'Could not save owners',
      },
      grantPermission: {
        title: 'Grant permission to user?',
        description:
          'The selected user does not have permission to manage budget lines. Assign the "Budget line owner" role?',
        confirm: 'Grant permission and save',
      },
    },
    display: {
      metricLabel: 'View',
      metric: {
        REAL_VS_BUDGET: 'Consumed / Budget',
        REALIZED: 'Realized',
        AVAILABLE: 'Balance',
        UTILIZATION: '% used',
        BUDGET: 'Budget',
      },
      densityLabel: 'Density',
      density: {
        COMPACT: 'Compact',
        STANDARD: 'Standard',
        COMFORTABLE: 'Roomy',
      },
      unitLabel: 'Unit',
      unit: {
        FULL: 'F',
        HUNDREDS: 'Hundreds',
        THOUSANDS: 'K',
        MILLIONS: 'M',
      },
      unitOption: {
        FULL: 'Full amount',
        THOUSANDS: 'Thousands (K)',
        MILLIONS: 'Millions (M)',
      },
      periodsLabel: 'Periods',
      periods: {
        all: 'All periods',
        fromNow: 'From this month on',
      },
      minTotalPlaceholder: 'Total ≥',
      minTotalClear: 'Clear total filter',
      minTotalApply: 'Apply',
      minTotalFilterTooltip: 'Filter by row total',
      showBars: 'Bars',
      hideZeroRows: 'Hide zero rows',
      clearFilters: 'Clear filters',
    },
    cellEditor: {
      applyLabel: 'Apply',
      applyCell: 'Cell',
      applyRow: 'Row',
      hint: 'Enter applies and moves down · Tab applies and moves right · Esc cancels',
    },
    messages: {
      saved: 'Budget saved',
      cleared: 'Values cleared',
      filledRight: 'Value replicated to the right',
      filledRow: "Value applied to the row's visible periods",
      selectAmountCell: 'Select an amount cell',
      emptyCell: 'Empty cell',
      noPeriods:
        'There are no open accounting periods within this budget’s effective range for this entity/book. Create or open periods before editing amounts.',
      readOnlyPermission:
        'You can view the spreadsheet but do not have permission to edit budget lines.',
      unsavedModeSwitch: 'Discard unsaved changes?',
      unsavedModeSwitchDescription:
        'Local changes will be lost and the spreadsheet will return to the last saved values.',
    },
    mode: {
      view: 'View',
      edit: 'Edit',
    },
    amountInputPlaceholder: '0.00 · 100k · 1M',
    globalAlerts: {
      title: 'Draft alerts',
      description:
        'Percentages applied to lines saved from this spreadsheet while you edit the budget.',
    },
    fillOffer: {
      title: 'Replicate value?',
      description: 'Replicate {{amount}} to the following months on this row?',
      confirm: 'Replicate right',
      dismiss: 'Not now',
    },
    validation: {
      dimensionRequired: 'Every row with a value needs at least one dimension',
      duplicateRows: 'Some rows have identical dimensions. Adjust or remove the duplicate.',
      hierarchyConflict:
        'Child lines total ({{childrenSum}}) exceeds the parent line "{{parent}}" limit ({{parentAmount}}).',
    },
    hierarchyError: {
      summary:
        'Parent line "{{parent}}" ({{parentAmount}}) does not cover the child lines: {{children}} ({{childrenSum}}).',
      generic:
        'Child line totals exceed the parent limit. Adjust the values or recalculate the parent line.',
      unnamedParent: 'parent line',
      unnamedChildren: 'child lines',
      highlightedRows:
        'Highlighted rows are in a hierarchy conflict. Adjust the values or recalculate the parent line.',
    },
    hierarchyOffer: {
      title: 'Recalculate parent line?',
      description:
        'Line "{{child}}" raised the child total to {{childrenSum}}, above the current parent line "{{parent}}" limit ({{parentAmount}}).',
      confirm: 'Recalculate parent',
      dismiss: 'Keep as is',
      applied: 'Parent line recalculated from child lines',
    },
    hierarchy: {
      groupToggle: 'Group hierarchy',
      expandAll: 'Expand all',
      collapseAll: 'Collapse all',
      childCount: '{{count}} lines',
      inheritsScope: 'inherits scope',
      dimensionAny: {
        costCenter: 'any cost center',
        department: 'any department',
        managerialAccount: 'any managerial account',
        glAccount: 'any GL account',
        segment: 'any segment',
      },
      allocatedSubline: 'Σ',
      allocatedRowTotal: 'Σ children',
      syncInlineCta: 'Update ceiling ({{amount}})',
      syncInlineDismiss: 'Dismiss',
      footerLeafTotal: 'Leaf total per period',
      kpiLeafHint: 'Σ allocated on leaf lines',
    },
    periodStatus: {
      closed: 'closed',
      current: 'in progress',
      projected: 'projected',
    },
    summary: {
      budgeted: 'Budgeted for period',
      budgetedHint: '{{periods}} months · {{lines}} lines',
      selectedLineHint: '{{periods}} months · {{line}}',
      realized: 'Realized',
      realizedHint: 'posted periods',
      committed: 'Committed',
      committedHint: 'open AP + recurring',
      consumption: 'Projected consumption',
      consumptionHint: 'real + committed / budget',
      available: 'Free balance',
      availableHint: 'not yet committed',
      attentionBadge: '{{count}} lines',
      attentionHint: 'above the alert limit this month',
      view: 'View',
    },
    footerTotals: {
      budgeted: 'Budgeted',
      realized: 'Realized',
      committed: 'Committed',
      consumption: 'Real + committed use',
    },
  },
  results: {
    payrollProjection: {
      tooltip:
        'Advisory signal from the FP&A payroll projection (rule {{rule}}, scenario {{ruleSetId}}, revision {{ruleSetRevision}}); this is not committed cash and no payroll is processed for {{month}}',
      adHoc: 'Ad-hoc pre-approvals',
      payroll: 'FP&A payroll projection',
      codes: {
        BUDGET_PAYROLL_PROJECTION_UNAVAILABLE:
          'Review the payroll scenario in FP&A → Modeling → Payroll. The entity default is used when no scenario is explicitly selected.',
        BUDGET_PAYROLL_PROJECTION_CONFIDENTIAL:
          'The aggregated payroll projection is hidden because your payroll data access is restricted.',
        BUDGET_PAYROLL_PROJECTION_MULTIPLE_RULE_SETS:
          'More than one active FP&A scenario contains payroll. Designate one scenario for Budget; until then, payroll projection is unavailable and no value is published.',
        BUDGET_PAYROLL_PROJECTION_CURRENCY_MISMATCH:
          'Change the FP&A payroll projection currency to the budget line currency (BRL in the MVP). Budget does not perform FX conversion, and no payroll value is published until it matches.',
        BUDGET_PAYROLL_PROJECTION_RUN_TYPE_UNAVAILABLE:
          'Review the payroll projection source; the run type required for deduplication was not provided, and no payroll value is published until it is corrected.',
        UNATTRIBUTABLE:
          'Review payroll accounting attribution in FP&A; part of the projection did not match a budget line.',
        FILTER_REFERENCE_UNRESOLVED:
          'Review the FP&A rule filters; a configured reference no longer exists.',
        HEADCOUNT_PLAN_UNATTRIBUTED:
          'Link headcount positions to accounts and dimensions so Budget can attribute them.',
        ENCARGOS_JURISDICTION_UNSUPPORTED:
          'Configure the entity jurisdiction burden policy before using the headcount plan.',
        ENCARGOS_POLICY_NOT_EFFECTIVE:
          'Adjust the burden policy effective dates to cover the projected period.',
        JOINERS_NOT_MODELLED:
          'Add future hires to the headcount plan; run-rate does not create them automatically.',
        UNRECOGNISED_RUBRIC: 'Classify unrecognized payroll rubrics to correct the recurring base.',
        PAYROLL_MANAGERIAL_UNRESOLVED: 'Map the payroll GL account to a managerial account.',
        UNMAPPED_COLLABORATOR:
          'Link the contractor to a party or vendor before projecting the cost.',
        PAYROLL_BASE_RUN_STALE:
          'Process a recent payroll; the base run is outside the run-rate window.',
        PAYROLL_TERMINATION_IDENTITY_AMBIGUOUS:
          'Complete the termination identity to avoid duplicate projected population.',
        PAYROLL_CURRENCY_MISMATCH:
          'Correct payroll currency to the book functional currency; projection does not convert amounts.',
        PAYROLL_PROVISION_MISSING:
          'Import or process missing provisions for employees used in the projection.',
        INSUFFICIENT_HISTORY:
          'Insufficient RPA history for the configured window and minimum occurrences.',
        UNAVAILABLE: 'CLT payroll unavailable. Import monthly payroll to project this component.',
        NO_SOURCE_DATA: 'No imported payroll of the enabled type exists in the configured window.',
        PAYROLL_EMPLOYEE_SKIPPED:
          'Review employees with a zero or negative computed amount in the base run.',
        PAYROLL_COLLABORATOR_STALE: 'Record a recent contractor bill to refresh the run-rate.',
        PAYROLL_COLLABORATOR_NO_EMISSION:
          'Review the contractor recurring template; it emitted no projectable months.',
        PAYROLL_REFUSED_COVERED_BY_RESIDUAL:
          'Review refused payroll coverage; FP&A retained it only in the residual projection.',
        PAYROLL_TEMPLATE_NOT_MODELLED:
          'Model the recurring payroll commitment in FP&A to project it explicitly.',
        PAYROLL_TEMPLATE_WINDOW_DRIFTED:
          'Align the recurring payroll template dates with the projected window.',
      } satisfies Record<BudgetPayrollProjectionCode, string>,
    },
    buckets: {
      realized: 'Realized',
      scheduled: 'Scheduled',
      committed: 'Committed',
      projected: 'Projected',
      available: 'Available',
    },
    bucketDescriptions: {
      realized: 'Paid and posted (GL)',
      scheduled: 'Approved and posted (GL), awaiting payment',
      committed: 'PO / recurring / exception — single source',
      projected: 'Pre-approval without PO — does not create commitment',
      available: 'Limit minus utilization',
    },
    totalLine: 'Row total',
    totalPerPeriod: 'Total per period',
    filteredEmpty: {
      title: 'No lines reach the filtered total',
      description: 'Adjust or clear the total filter to see the budget lines.',
    },
    empty: {
      title: 'This budget has no lines yet',
      description: 'Edit the budget to add lines and limits.',
    },
    cellDetailHint:
      'Click a cell to inspect realized, scheduled, committed, and projected amounts.',
    inheritsScope: '↳ inherits scope — {{label}}',
    legend: {
      withinLimit: 'Within limit',
      forecastBreach: 'Exposure over limit',
      realizedBreach: 'Realized + scheduled + committed over limit',
      nearLimit: 'Alert limit',
      overLimit: 'Over budget',
    },
    signals: {
      lineForecastPill: 'Projected over',
      lineBreachPill: 'Over limit',
      scopeHint:
        'Row signals reflect cumulative totals; each cell uses the same criteria for its period. ▲ = exposure (realized + scheduled + committed + ad-hoc projection) over limit; payroll projection is advisory only. ● = realized + scheduled + committed over limit.',
    },
    drawer: {
      limitTitle: '{{amount}} limit',
      singleCell: 'Single cell · period {{period}}',
      utilized: '{{amount}} utilized',
      summary: '{{pct}} of {{limit}} · {{available}} available',
      availableTitle: 'Available within limit',
      budgetSummary: 'of {{limit}} budgeted · {{pct}} consumed',
      previousPeriod: 'Previous period',
      nextPeriod: 'Next period',
      thresholdMarker: '{{threshold}}% warning',
      openApHint: 'Open AP',
      searchPlaceholder: 'Search vendor or document',
      eventRange: '{{start}}–{{end}} of {{total}} events',
      explainedAbove: '80% of consumption explained above',
      remainingEvents: '{{count}} remaining events · {{amount}}',
      footerCount: '{{count}} events · total',
      export: 'Export',
      exportSuccess: 'Events exported',
      exportError: 'Could not export events',
      emptySearch: 'No events match this search',
      inheritsRule: 'Inherits the total-limit rule',
      tabs: {
        events: 'Events',
        managerialAccounts: 'By managerial account',
        limitRule: 'Limit rule',
      },
      columns: {
        type: 'Type',
        vendorDocument: 'Vendor / document',
        competence: 'Competence',
        status: 'Status',
        amount: 'Amount',
        limitPct: '% limit',
      },
      exceptionLimitSummary:
        'Includes {{exceptionAmount}} from approved exception · effective limit {{effectiveLimit}} ({{effectivePct}} incl. projected)',
      eventTypes: {
        payrollClt: 'CLT payroll',
        payrollRpa: 'RPA payroll',
        payrollProLabore: 'Pro-labore',
        plannedClt: 'Planned CLT position',
        plannedPj: 'Planned PJ position',
        bill: 'Bill',
        recurringBill: 'Recurring bill',
        purchaseOrder: 'Purchase order',
        commitment: 'Commitment',
      },
      eventsTitle: 'Events impacting the limit',
      limitMarker: '100%',
      exposurePct: '{{pct}} incl. projected',
      status: {
        within: 'Within limit',
        warning: 'Attention · above {{threshold}}%',
        over: 'Exposure over limit',
        noLimit: 'No limit defined',
      },
    },
    loadError: 'Could not load budget results.',
    hierarchyUnavailable: {
      title: 'Totals temporarily unavailable',
      description: 'The complete budget line hierarchy could not be validated.',
    },
  },
  tabs: {
    lines: 'Budget lines',
    costCenters: 'By cost center',
    alerts: 'Alerts',
    settings: 'Settings',
  },
  actions: {
    newLine: 'New budget line',
    save: 'Save',
    saveSettings: 'Save settings',
    requestException: 'Request exception',
    requestExceptionAgain: 'Request again',
    approveException: 'Approve exception',
    rejectException: 'Reject exception',
    edit: 'Edit',
    editBudget: 'Edit budget',
    archive: 'Archive',
    dismiss: 'Dismiss',
    cancel: 'Cancel',
    close: 'Close',
    makeActive: 'Make active',
  },
  fields: {
    entity: 'Entity',
    book: 'Book',
    period: 'Period',
    startPeriod: 'Start period',
    endPeriod: 'End period',
    dimensionType: 'Scope',
    costCenter: 'Cost center',
    department: 'Department',
    managerialAccount: 'Managerial account',
    glAccount: 'GL account',
    glAccountPlaceholder: 'Select a GL account',
    customSegment: 'Custom field',
    customSegmentPlaceholder: 'Select a field',
    customSegmentValue: 'Field value',
    customSegmentValuePlaceholder: 'Select a value',
    allSegments: 'All fields',
    allSegmentValues: 'All values',
    amount: 'Total limit',
    amountPlaceholder: 'e.g. 4200000, 4.2M or 100k',
    perPeriodAmount: 'Per-period limit',
    perPeriodAmountPlaceholder: 'e.g. 350000 or 350k',
    perPeriodEnforcementMode: 'Per-period control',
    enforcementMode: 'Total-limit control',
    alertWarning: 'Warning',
    alertCritical: 'Critical',
    designatedBook: 'Designated budget book',
    unmatchedSpendPolicy: 'Unmatched spend policy',
    blockEnforcementWhileStale: 'Block enforcement while stale',
    justification: 'Justification',
  },
  dimensionType: {
    COST_CENTER: 'Cost center',
    DEPARTMENT: 'Department',
    COMPANY: 'Company',
  },
  enforcementMode: {
    HARD_BLOCK: 'Hard block',
    SOFT_BLOCK: 'Soft block',
    ALERT_ONLY: 'Alert only',
  },
  status: {
    ACTIVE: 'Active',
    INACTIVE: 'Inactive',
    ARCHIVED: 'Archived',
    PENDING: 'Pending',
    APPROVED: 'Approved',
    REJECTED: 'Rejected',
  },
  recomputeStatus: {
    PENDING: 'Recompute pending',
    RUNNING: 'Recomputing utilization',
    COMPLETED: 'Utilization up to date',
    FAILED: 'Recompute failed',
  },
  labels: {
    company: 'Company',
    allCategories: 'All categories',
    allManagerialAccounts: 'All managerial accounts',
    allGlAccounts: 'All GL accounts',
    allSegments: 'All custom fields',
    allSegmentValues: 'All values',
    fallbackLine: 'Residual (Other)',
    required: 'Required',
    optional: 'Optional',
    withinBudget: 'Within budget',
    withAlert: 'With alert',
    warningThreshold: 'Warning range',
    criticalThreshold: 'Critical range',
    budgetSummary: 'Budget summary',
    noEntitySelected: 'Select an entity to load budgets',
    noLines: 'No budget lines found',
    noAlerts: 'No active alerts',
    loading: 'Loading...',
    utilization: 'Utilization',
    utilizationFormula: 'Utilization (used + committed)',
    availableAfterProjected: 'Available after projected',
    available: 'Available',
    used: 'Used',
    usedPlusAvailable: 'Used + available',
    subAccounts: '{{count}} sub-accounts',
    exceeded: 'Exceeded',
    exceededBy: 'Exceeded by',
    currentException: 'Current exception',
    softBlockTitle: 'Budget exceeded',
    exceptionApprovedTitle: 'Exception approved',
    exceptionRejectedTitle: 'Exception rejected',
    hardBlockTitle: 'Budget exceeded — submission blocked',
    hardBlockDescription:
      'This bill was automatically rejected because it exceeds a hard budget limit. Exceptions are not allowed. Adjust the bill or update the budget line, then send for approval again.',
    hardBlockProactiveDescription:
      'This bill exceeds a hard budget limit. Submission for approval will be blocked and exceptions are not allowed. Adjust the bill or update the budget line before submitting.',
    hardBlockActions:
      'Reduce the amount, change classification dimensions, or increase the budget line limit.',
    projectedExceedTitle: 'Projected utilization exceeds budget',
    projectedExceedDescription:
      'Including realized, committed, and other ad-hoc pre-approval bills, the line would exceed its limit. Review before submitting — this does not block submission.',
    softBlockDescription:
      'This bill exceeds the configured budget and requires an approved exception before final approval can continue.',
    exceptionPending: 'Budget exception requested. Awaiting approval.',
    exceptionRejected: 'Request rejected. Request again to continue.',
    exceptionApprovedFinalize:
      'Budget exception approved. You can now finalize the approval of this bill.',
    exceptionAppliedSummary:
      'Approved exception added {{amount}} to the line limit (effective limit {{effectiveLimit}}).',
    exceptionAppliedLabel: 'with approved exception',
    budgetLine: 'Budget line',
    assessedPeriod: 'Evaluated period',
    lineLimitSuffix: 'of limit',
    exceededTotalLimit: 'Total limit exceeded by {{amount}}',
    exceededPeriodLimit: 'Per-period limit exceeded by {{amount}}',
    scopeConfiguration: 'Selected scope configuration',
    excess: 'Excess',
    limitLegend: 'Limit ({{amount}})',
    alertScopeTotal: 'Applies to accumulated utilization of the total limit.',
    alertScopePeriod: 'Applies to utilization of the per-period limit.',
    inheritsTotalControl: 'Uses the same rule defined for the total limit.',
  },
  alerts: {
    draftTitle: 'Draft Budget',
    draftDescription:
      'You can view limits and utilization based on the draft, but you will need to activate the budget for it to apply in other modules.',
    reforecastDraftDescription:
      'This reforecast is not yet the enforcing budget. The plan in force is {{enforcing}}.',
    enforcingTitle: 'This budget is enforcing',
    enforcingDescription:
      'Promoted from reforecast {{revision}}, which replaced {{previous}}. Approved by {{approver}} on {{date}}.',
    supersededTitle: 'Superseded budget',
    supersededDescription:
      'This plan was replaced by an approved reforecast. Consumption now points at the new enforcing budget. This plan stays readable, but current utilization is on the replacement plan and it no longer limits spending.',
    statusWarning: 'This budget is in status {{status}}',
    statusWarningDescription: 'Activate the budget to unlock the main actions.',
    payrollDataRestricted: 'Attention: payroll amounts are not being displayed.',
    recomputeStaleDescription:
      'Utilization is still recomputing in the background. Some budget-control results may be temporarily blocked.',
    recomputeWarningTitle: 'Recompute completed with warnings',
    recomputeWarningDescription:
      'Some recurring templates were skipped because their classification has no GL account split. Fix their classification to include them in committed.',
    showDetails_one: 'Show {{count}} template',
    showDetails_other: 'Show {{count}} templates',
    hideDetails: 'Hide',
  },
  tooltips: {
    dimensionType:
      'Defines whether this line controls the budget by cost center, department, or entire company.',
    managerialAccount:
      'Managerial account covered by the line. Use it to restrict the budget to a specific accounting category.',
    glAccount:
      'GL account covered by the line. Use it with the managerial account to further restrict the budget.',
    customSegment: 'Custom field used as an additional filter for this budget line.',
    customSegmentValue: 'Specific custom field value covered by this budget line.',
    costCenter: 'Cost center that consumes this budget when cost center is the selected scope.',
    department: 'Department that consumes this budget when department is the selected scope.',
    startPeriod: 'First accounting period in which the budget line is effective.',
    endPeriod: 'Last accounting period covered by the budget line.',
    amount:
      'Maximum accumulated value allowed for the selected range. Shortcuts: 100k, 1M, 10M, 1B.',
    perPeriodAmount:
      'Maximum value allowed in each period. Shortcuts: 100k, 1M, 10M, 1B. Leave blank to control only by the total limit.',
    enforcementMode: 'Rule applied when spending exceeds the configured total limit.',
    perPeriodEnforcementMode:
      'Rule applied when spending exceeds a period limit. Without a selection, it inherits the total-limit control.',
    alertWarning:
      'Utilization percentage that triggers the first alert before a block or exception.',
    alertCritical: 'Utilization percentage considered critical and requiring immediate attention.',
  },
  table: {
    dimension: 'Dimension',
    scope: 'Scope',
    coverage: 'Coverage',
    limit: 'Limit',
    perPeriodLimit: 'Per-period limit',
    utilized: 'Used',
    available: 'Available',
    utilization: 'Utilization',
    mode: 'Mode',
  },
  detail: {
    commitmentsTitle: 'Commitments',
    billsTitle: 'Posted bills',
    eventsEmpty: 'No events in this period.',
    commitmentsEmpty: 'No open commitments in this period.',
    billsEmpty: 'No posted bills in this period.',
    loadError: 'Could not load line details.',
    eventsPagination: 'Showing {{shown}} of {{total}} events (page {{page}} of {{totalPages}})',
    eventsPrevPage: 'Previous',
    eventsNextPage: 'Next',
    columns: {
      source: 'Source',
      reference: 'Reference',
      period: 'Period',
      openAmount: 'Open amount',
      status: 'Status',
      vendor: 'Vendor',
      description: 'Description',
      postedAmount: 'Posted amount',
      date: 'Date',
      bucket: 'Bucket',
    },
    sort: {
      dateDesc: 'Newest date',
      amountDesc: 'Highest amount',
      amountAsc: 'Lowest amount',
    },
    commitmentSource: {
      PURCHASE_ORDER: 'Purchase order',
      RECURRING_BILL: 'Recurring bill',
      BUDGET_EXCEPTION: 'Budget exception',
    },
    commitmentStatus: {
      OPEN: 'Open',
      PARTIALLY_SETTLED: 'Partially settled',
      SETTLED: 'Settled',
      CANCELLED: 'Cancelled',
    },
    exceptionCommitmentTag: 'Approved exception',
    exceptionCommitmentReference: 'Exception bill:',
  },
  filters: {
    all: 'All',
    alert: 'With alert',
    within: 'Within budget',
  },
  tone: {
    critical: 'Attention needed',
    warning: 'Alert',
    normal: 'Normal',
  },
  insights: {
    title: 'Budget intelligence',
    healthy: 'No cost center is above the configured limits for the selected period.',
    alertLine:
      '{{line}} is at {{utilization}} utilization; remaining balance: {{available}}. Review alerts and exceptions before close.',
    viewAlerts: 'View alerts',
  },
  metrics: {
    totalBudget: 'Total budget',
    realized: 'Used',
    available: 'Available',
    alertLines: 'Lines with alert',
    activeLines: '{{count}} active lines',
    utilizationShare: '{{percent}} of budget',
    remaining: 'Remaining balance',
    criticalLines: '{{count}} critical',
  },
  sections: {
    linesTitle: 'Budget lines',
    linesDescription: 'Budget by dimension. Click a line to inspect details.',
    costCentersTitle: 'By cost center',
    costCentersDescription: 'Consolidated view of used amount and balance by cost center.',
    alertsTitle: 'Active alerts',
    alertsDescription: 'Exceptions, blocks, and overrun forecasts.',
  },
  dialog: {
    createTitle: 'New budget line',
    editTitle: 'Edit budget line',
    description: 'Configure the limit, covered period range, and control rules.',
    editBudgetTitle: 'Edit budget',
    editBudgetDescription: 'Update the budget name, description, and effective window.',
    deleteLineTitle: 'Delete budget line?',
    deleteLineDescription: 'The line "{{name}}" will be archived and stop affecting the budget.',
    hierarchyHint:
      'More specific lines roll up to parent lines on the same dimension. Child line totals cannot exceed the parent limit. Unmatched spend is routed to the Other fallback line.',
    exceptionTitle: 'Request budget exception',
    exceptionDescription:
      'Explain why the bill should continue even though it exceeds the configured limit.',
    approveExceptionTitle: 'Approve budget exception',
    rejectExceptionConfirm: 'Reject this budget exception?',
    rejectExceptionDescription:
      'The request will be rejected and can no longer be approved. Confirm to continue.',
    payable: 'Payable',
    value: 'Value',
    costCenterCategory: 'CC / Category',
    exceptionImpact:
      'Approving this exception records {{amount}} of overage in the budget history.',
    resolutionPlaceholder: 'Example: additional campaign approved by leadership...',
  },
  settings: {
    title: 'Global defaults',
    description: 'Define the designated book and default thresholds for new budget lines.',
    budgetBookTitle: 'Budget base book',
    budgetBookDescription: 'Book used as the reference for new budget lines.',
    enforcementTitle: 'Default budget control',
    enforcementDescription: 'What happens when a bill exceeds the budget.',
    thresholdTitle: 'Early alert threshold',
    thresholdDescription: 'Trigger an alert when utilization reaches this percentage.',
    rolloverTitle: 'Unused balance rollover',
    rolloverDescription: 'What happens with remaining balance at the end of the period.',
    rolloverCurrent: 'Transfer to next period. Balance accumulates in the following period.',
  },
  enforcementModeDescription: {
    HARD_BLOCK: 'Blocks posting or approval when the limit is exceeded.',
    SOFT_BLOCK: 'Allows the flow to continue only with an exception or special approval.',
    ALERT_ONLY: 'Does not block the flow; only flags and notifies responsible users.',
  },
  hints: {
    monthlyCoverage: 'Monthly',
    rangedCoverage: 'Multi-period',
  },
  messages: {
    settingsSaved: 'Budget settings saved successfully',
    budgetCreated: 'Budget created successfully',
    budgetDuplicated: 'Budget duplicated successfully',
    budgetActivated: 'Budget activated successfully',
    budgetDeactivated: 'Budget inactivated successfully',
    budgetUtilizationRecomputePending:
      'Budget utilization will be recomputed in the background. Values may take a few minutes to update.',
    budgetArchived: 'Budget archived successfully',
    budgetUpdated: 'Budget updated successfully',
    bulkUpdated: 'Lines updated successfully',
    bulkArchived: 'Lines archived successfully',
    lineSaved: 'Budget line saved successfully',
    lineArchived: 'Budget line archived successfully',
    exceptionRequested: 'Budget exception requested',
    exceptionResolved: 'Budget exception updated successfully',
    exceptionRejected: 'Budget exception rejected successfully',
    loadError: 'Failed to load budget data',
    saveError: 'Failed to save budget data',
    linePeriodInvalid:
      'The selected start/end period is not available for this entity/book. Review the periods and try again.',
    reviewPeriods: 'Review periods',
    openChartOfAccounts: 'Open chart of accounts',
    invalidGlAccount: {
      generic:
        'The selected GL account is not valid for this entity and book. Choose another account or review the accounting setup.',
      coaVersionNotAssigned:
        'No active chart of accounts is linked to this entity and book. Activate the CoA in the accounting module, then save again.',
      accountNotFound:
        'The selected GL account was not found. Refresh the page, pick another account, and try again.',
      accountNotPostable:
        'The selected account does not allow postings. Choose a postable (analytical) account from the list.',
      accountInactive:
        'The selected account is inactive. Choose an active account or reactivate it in the chart of accounts.',
      versionMismatch:
        'The account belongs to a different CoA version than the one active for this entity/book. Select an account from the active version or update the CoA activation.',
    },
    activateZeroAmountLines:
      'Cannot activate budget: all active lines must have a total limit greater than zero.',
    revisionNotActivatable:
      'A reforecast cannot be activated directly. Use approval to make it the enforcing budget.',
    reforecastPromoted: 'Reforecast approved and now enforcing.',
    reforecastCreated:
      'Reforecast created. Edit the amounts, then approve to switch the enforcing budget.',
    reforecastDefaultName: 'Reforecast {{start}}–{{end}}',
    reforecastCreateTitle: 'Create reforecast',
    reforecastCreateDescription:
      'Creates a draft copy of the enforcing budget with the current limits. Edit it, then approve to switch the plan that limits spending.',
    reforecastCreateAmountsHint:
      'Period amounts start as a copy of the enforcing budget. You can edit them after creating.',
    reforecastCreatePartialWarning:
      'This window does not cover every period of the enforcing budget. You can still edit the reforecast, but approval will be refused until coverage is complete.',
    reforecastPromoteTitle: 'Approve reforecast and switch the enforcing budget',
    reforecastPromoteDescription:
      '{{current}} will stop enforcing spending limits. Reforecast {{revision}} will become the enforcing budget.',
    reforecastPromoteConfirm: 'Approve and switch',
    reforecastPromoteCancel: 'Cancel',
    reforecastReviewAgain: 'Review updated preview',
    reforecastPreviewRequired: 'Load and review the preview before approving.',
    reforecastPeriod: 'Period',
    reforecastPreviousAmount: 'Current limit',
    reforecastNextAmount: 'New limit',
    reforecastSelfApproval:
      'Approval requires a second user. Anyone who made a material change to this reforecast cannot promote it.',
    reforecastPreparerRequired:
      'Save a line or amount change on this reforecast before approving. This copy has no recorded preparer.',
    reforecastPartialCoverage:
      'This reforecast does not cover every period of the enforcing budget. Missing periods: {{periods}}.',
    validation: {
      budgetBookRequired: 'Select an accounting book to create the budget.',
      budgetBookNotLinked:
        'The designated accounting book is not linked to this entity. Review budget settings.',
      effectiveFromInvalid: 'The effective start date is invalid.',
      effectiveUntilInvalid: 'The effective end date is invalid.',
      effectiveRangeInvalid: 'The start date cannot be after the end date.',
      effectiveUntilRequired: 'Provide an effective end date.',
      endPeriodRequired: 'Provide an end period for template budgets.',
      archivedBudget: 'Archived budgets cannot be edited.',
      archivedBudgetActivate: 'Archived budgets cannot be activated.',
      supersededNotActivatable: 'Superseded budgets cannot be activated.',
      supersededImmutable: 'Superseded budgets cannot be changed.',
      reforecastPartialCoverage:
        'This reforecast does not cover every period of the enforcing budget. Missing periods: {{periods}}.',
      reforecastNotPromotable:
        'Only a draft reforecast in the same version family can be promoted.',
      reforecastNoActive:
        'There is no active budget on this entity and book for the reforecast to replace.',
      reforecastPromotionConflict:
        'The reforecast amounts or enforcing budget changed. Review the updated preview before approving.',
      reforecastSelfApproval:
        'Approval requires a second user. Anyone who made a material change to this reforecast cannot promote it.',
      reforecastPreparerRequired:
        'Save a line or amount change on this reforecast before approving. This copy has no recorded preparer.',
      reforecastEnforcingCannotDeactivate:
        'An enforcing reforecast cannot be deactivated. Create a new reforecast and approve it to switch the budget that limits spending.',
      deactivateBeforeArchive: 'Deactivate the budget before archiving it.',
      unsupportedTemplateScope: 'The selected template is not supported.',
      perPeriodEnforcementRequiresLimit:
        'Set a per-period limit before changing the per-period enforcement mode.',
      alertThresholdWarningRange: 'The warning alert must be between 0% and 100%.',
      alertThresholdCriticalRange: 'The critical alert must be between 0% and 100%.',
      alertThresholdOrder: 'The warning alert must be less than or equal to the critical alert.',
      noPeriodsInEffectiveRange:
        'There are no accounting periods in the selected effective range. Create or open periods and try again.',
      contiguousEffectivePeriods:
        'The budget effective range must cover contiguous periods without gaps.',
      noActiveCostCenters:
        'There are no active cost centers for the selected entity. Add cost centers before using this template.',
      noActiveDepartments:
        'There are no active departments for the selected entity. Add departments before using this template.',
      noActiveManagerialAccounts:
        'There are no active expense managerial accounts for the selected entity. Set up the managerial chart of accounts before using this template.',
      fallbackLineScopeLocked: 'The Outros fallback line scope cannot be changed.',
      costCenterDimensionInvalid:
        'Cost center lines require a cost center and must not include a department.',
      departmentDimensionInvalid:
        'Department lines require a department and must not include a cost center.',
      companyDimensionInvalid: 'Company lines must not include a cost center or department.',
      amountNonNegative: 'The budget amount must be zero or greater.',
      amountPositive: 'The total limit must be greater than zero.',
      amountInvalidScale: 'The amount must have at most two decimal places.',
      perPeriodAmountPositive: 'The per-period limit must be greater than zero.',
      periodLimitsMissingPeriodId: 'Each per-period limit needs a valid period.',
      periodLimitsDuplicatePeriodId: 'Per-period limits contain duplicate periods.',
      periodLimitsInvalidAmount:
        'Each per-period limit must be a valid number with at most two decimal places.',
      periodLimitsNegativeAmount: 'Per-period limits must be zero or greater.',
      periodLimitsOutsideWindow:
        'One or more per-period limits are outside the line window. Review the periods.',
      budgetIdRequired: 'The budget for this line is required.',
      archivedBudgetLines: 'Lines on an archived budget cannot be changed.',
      lineEntityBookMismatch: 'The line entity/book must match the parent budget.',
      invalidCostCenter: 'Invalid cost center for the selected entity.',
      invalidDepartment: 'Invalid department for the selected entity.',
      costCenterInactive: 'The selected cost center is inactive. Choose an active cost center.',
      departmentInactive: 'The selected department is inactive. Choose an active department.',
      invalidManagerialAccount: 'Invalid managerial account for the selected entity.',
      segmentValueRequiresSegment: 'Select a segment before choosing a value.',
      invalidCustomSegment: 'Invalid custom segment for the selected entity.',
      invalidCustomSegmentValue: 'Invalid segment value for the selected segment.',
      segmentHasTextValues:
        'Segment {{segmentName}} has bills classified with typed values. Reclassify those bills before budgeting it.',
      startAfterEndPeriod: 'The start period cannot be after the end period.',
      noPeriodsInLineRange: 'There are no accounting periods in the selected line range.',
      contiguousLinePeriods: 'The line period range must be contiguous.',
      totalBelowPeriodLimits: 'The total limit cannot be less than the sum of per-period limits.',
      stampedPeriodConflict:
        'This change would drop a derivation-stamped period. Send dropStampedPeriodIds naming each period to remove.',
      totalLimitCannotBeCleared: 'The total limit cannot be cleared.',
      fallbackLineCannotArchive: 'The Outros fallback line cannot be archived.',
      selectLinesToUpdate: 'Select at least one line to update.',
      selectLinesToArchive: 'Select at least one line to archive.',
      chooseFieldsToUpdate: 'Choose at least one field to update.',
      archivedBudgetLine: 'Archived lines cannot be changed.',
      currencyBrlOnly: 'Budget lines must use BRL for now.',
      resyncRequiresActive: 'Only an active budget can be resynchronized.',
      resyncRequiresDesignatedBook:
        'This budget does not use the entity’s designated budget-control book.',
      recurringBlockedWhileStale:
        'The budget utilization recompute is pending. Wait for it to finish (or run the budget reconciliation) and try saving the recurring template again.',
      ownerMissingPermission: 'The selected user does not have permission to manage budget lines.',
      ownerTransferForbidden: 'Only the current budget owner can transfer ownership.',
      lineOwnerModifyForbidden:
        'Only the budget owner or an authorized line owner can change this line’s limits.',
      lineOwnerLimitExceeded: 'The amount exceeds the maximum limit allowed for this line owner.',
      targetOwnerInvalid: 'The new owner was not found or is inactive.',
      bulkAmountCannotBeCleared: 'This amount cannot be cleared in a bulk change.',
      bulkAmountInvalidNumber: 'Enter a valid number for this bulk change.',
      bulkAmountMustBePositive: 'The bulk change amount must be positive.',
      bulkAmountUnsupportedOperation: 'Unsupported bulk change operation.',
      bulkAmountResultNotPositive:
        'The result of the bulk change must be greater than zero. Review the selected values.',
      exceptionTargetRequired:
        'Provide exactly one document (bill, purchase request, purchase order, or recurring bill) for the exception.',
      exceptionNotBlocked: 'This document is not blocked by a budget that requires an exception.',
      exceptionNoExcess: 'No budget excess was found for the selected budget line.',
      exceptionRecurringRequiresLimitIncrease:
        'Recurring budget requests from a hard block must use the Limit increase type.',
      exceptionAlreadyResolved: 'This budget exception has already been resolved.',
      exceptionRecurringLimitIncreaseOnly:
        'Recurring hard-block requests can only be approved as Limit increase.',
      ambiguousOverlappingRange:
        'Overlapping budget ranges with the same specificity must be fully nested or disjoint.',
      recurringTemplateHardBlock:
        'This recurring bill template would exceed the configured budget limit. Reduce the period limit, edit budget lines, or change classifications before saving.',
      exceptionBillHasPendingRequest:
        'This bill already has a pending budget exception request. Resolve it before requesting another.',
    },
    activationSwitchTitle: 'Replace active budget',
    activationSwitchDescription:
      'An active budget already exists for this entity and book. Continuing will move the current active budget back to draft so only one plan stays active for this book.',
    activationSwitchConfirm: 'Replace active budget',
    activationSwitchCancel: 'Cancel',
    activationSwitchBookLabel: 'Book',
  },
} as const;
